import { splitShootingMethod } from '../../state/shootingMethod';
import { effectiveMotionGraphics, normalizeMotionGraphics } from '../../state/motionGraphics';
import { normalizeDialect } from '../../state/dialect';
import { normalizeTargetKey } from '../../state/targetAudience';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../../hooks/useToast';
import { resolveAiSourceUrl, resolveMaterialUrls } from '../../../../features/materials/materialUrl';
import { fetchAsinMaterials, saveImageToTemporary, fetchMaterialById } from '../../../../features/materials/api';
import { getProduct } from '../../../../features/products/api';
import { getScenario, prepareMotionScenario, updateScenario, type MotionBgmChoice } from '../../../../features/scenarios/api';
import {
  MOTION_DURATION_TOO_LONG_MESSAGE, isMotionEngineScenario, isMotionPrepareShell, readMotionPrepare, rememberMotionBgmJob,
} from '../motionScenario';
import {
  deriveLoadedVideoGenerationMode, deriveProductionModeWriteFields,
} from '../../../../features/scenarios/productionMode';
import { useScenarioFieldConfig } from '../../../../hooks/usePartnerBranding';
import { calculateAspectRatio } from '../../../materials/shared/materialUtils';
import type { GenerationAsset, CastingActor, SeedancePresetActorItem } from '../../state/useGenerationState';
import type { Scenario, ScenarioResponse, Mode, ScenarioLocation } from '../types';
import { toUiDialogueLines } from '../dialogueLines';
import { toUiTelopLines } from '../telopLines';
import { applyScriptImportToPayload, SCRIPT_IMPORT_MOTION_REASON, type ImportedScriptState } from '../scriptImport';
import type { UseProductSelectionReturn } from './useProductSelection';
import type { ScenarioFormState } from './useScenarioFormState';
import { SCENARIO_JOB_STORAGE_KEY, SCENARIO_GENERATE_MUTATION_KEY } from '../constants';
import {
  requestGenerateScenario, pollScenarioJob, isShellFlowOutcome, type ShellScenario,
  isScenarioJobInFlight, settleScenarioJob, clearScenarioJobResumeKeyIfSettled,
  resolveCastPayloadActors, shouldSendSeedancePresetActors,
} from '../utils';
import {
  startTrack, getTrack, getAttachedTrack, finishTrack, beginHandler, endHandler,
  setPendingEventProbe, useAttachedScenarioTrack,
  type InFlightShellFlow, type ScenarioGenerationTrack, type ScenarioPostWrite,
} from '../scenarioGenerationTracks';
import { resolveErrorMessage } from '../../../../utils/getErrorMessage';
import { addToPromptHistory, PROMPT_HISTORY_KEYS } from '../../../../utils/promptHistory';
import { waitForNextPoll } from '../../../../utils/polling';

import { scenarioModelDisplayName } from '../scenarioModelCatalog';
import { DEFAULT_VIDEO_MODEL, useGenerationState } from '../../state/useGenerationState';
import { useSceneStateContext } from './SceneStateContext';
import { isStorageQuotaError, storageQuotaErrorMessage } from '../../../../utils/storageQuotaError';
import { useGenerationModels } from '../../../../features/generation-models/hooks';
import {
  computeEvenSplitDefaultDuration,
  computeStoryboardTotalDuration,
  parseIntendedDurationSeconds,
  resolvePerSceneDefaultDuration,
} from '../perSceneDefaultDuration';

function resolveUsableVideoModel(...candidates: Array<string | null | undefined>): string {
  const usable = candidates
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find((value) => value.length > 0 && value !== 'random');
  return usable || DEFAULT_VIDEO_MODEL;
}

type ShellGenerationSnapshot = {
  characterCountKey?: string;
  characterCount?: number;
  castingModels?: CastingActor[];
  seedancePresetActors?: SeedancePresetActorItem[];
};

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

// ── 開いているシナリオタブの保持 (sessionStorage) ──────────────────────────
// 開いているシナリオの一覧はコンポーネントの state (lastResult.scenarios) にしか無く、
// 動画エディタなど別ページへ移って戻ると 1 件 (URL のシナリオ) だけになっていた。
// ブラウザタブ単位の sessionStorage に「開いているタブの ID 一覧 + アクティブ ID」を保存し、
// state が空の状態で URL のシナリオがその一覧に含まれていれば、元の順序で全タブを読み直す。
// (URL は今までどおり /studio/video/{アクティブなシナリオID} のまま。localStorage ではなく
// sessionStorage なので、別のブラウザタブで開いた作業内容とは混ざらない)
const OPEN_TABS_STORAGE_KEY = 'scenario_workspace_open_tabs';

interface StoredOpenTabs {
  ids: string[];
  activeId: string | null;
}

function readStoredOpenTabs(): StoredOpenTabs | null {
  try {
    const raw = sessionStorage.getItem(OPEN_TABS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredOpenTabs>;
    const ids = Array.isArray(parsed.ids) ? parsed.ids.filter((id): id is string => typeof id === 'string') : [];
    if (ids.length === 0) return null;
    return { ids, activeId: typeof parsed.activeId === 'string' ? parsed.activeId : null };
  } catch {
    return null;
  }
}

function writeStoredOpenTabs(value: StoredOpenTabs | null): void {
  try {
    if (value) {
      sessionStorage.setItem(OPEN_TABS_STORAGE_KEY, JSON.stringify(value));
    } else {
      sessionStorage.removeItem(OPEN_TABS_STORAGE_KEY);
    }
  } catch {
    // プライベートモード等で使えない場合は保持しない (復元されないだけ)
  }
}

interface UseScenarioGenerationParams {
  // Navigation / routing
  scenarioIdFromPath?: string;

  /** 現在のユーザー (app_users.user_uid)。生成中シェルのプレースホルダに所有者として入れる。 */
  currentUserId?: string;

  // lastResult – kept as component-level state to avoid circular deps
  lastResult: ScenarioResponse | null;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;

  // Mode
  mode: Mode;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;

  /** 商品入力フォーム一式 (useProductSelection の返り値をそのまま渡す)。 */
  product: UseProductSelectionReturn;

  /** シナリオ設定フォーム一式 (useScenarioFormState の返り値をそのまま渡す)。 */
  form: ScenarioFormState;

  /**
   * 現在の動画生成モード。prompt / scenario でストアのキーが異なり、親が mode で
   * 出し分けているため props で受け取る (useGenerationState から直接読まない)。
   */
  videoGenerationMode: string | null;
  // 読み込んだシナリオの表示モード同期用 (localStorage に書かない非永続セッター)。
  // 生成デフォルトモードを開いたシナリオで上書きしないため、永続セッターは渡さない。
  setVideoGenerationModeTransient: (mode: 'frame' | 'reference' | 'storyboard') => void;

  // UI state setters
  setExpandedVideos: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setExpandedNarrations: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setIsProductSectionOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSceneVideoSettings: React.Dispatch<React.SetStateAction<Record<string, { duration: number; generateAudio: boolean; videoModel?: string; resolution?: string }>>>;
  /** 保存済みシナリオの声からの音声AIモデル復元 (localStorage には書かない) */
  setRecommendedNarrator: (narrator: { gender?: string; age?: string }) => void;

  // Temp materials cache (for handleLoadScenario)
  tempMaterials: { data?: { pages: Array<{ items: any[] }> } };

  // Validation refs
  productSelectRef: React.RefObject<HTMLButtonElement | null>;
  setShowProductTooltip: React.Dispatch<React.SetStateAction<boolean>>;

  // Post-generation callbacks
  handleBatchGenerateFirstFrames: (params: { scenarios: Scenario[]; scenarioId: string }) => void;
  // 「ストーリーボードを自動生成」: 渡されたシナリオ群に対し絵コンテ静止画生成を起動する。
  // batchId を渡すと、各絵コンテジョブを集約通知バッチのスロットとして数える。
  autoGenerateStoryboards: (scenarios: Scenario[], batchId?: string) => void;

  // 全編モーション (映像スタイルの option metadata.production_engine === 'motion'・§10.5)。
  // true のとき生成ボタンは即送信せず、BGM の選択モーダルを開く (requestMotionBgmChoice)。
  isMotionEngine?: boolean;
  /** 選択中トーンが歌モノか。全編モーションとは一体化しないので送らせない */
  isLyricsTone?: boolean;
  /** 全編モーションで想定尺が 60 秒を超えているか (全編を 1 本で作れないので送らせない・§10.6) */
  isMotionDurationTooLong?: boolean;
  /** BGM の選択モーダルを開く。利用者が選んだら submit(choice) を呼ぶ */
  requestMotionBgmChoice?: (submit: (choice: MotionBgmChoice) => void) => void;
  /** 読み込んだ台本 (docs/features/scenario-script-import-plan.md §6.2)。非 null なら 3 分岐 (asin / manual / custom) すべての
   *  ペイロードに imported_script を付け、count=1 に固定し dialect を送らない。 */
  importedScript?: ImportedScriptState | null;
}

// ────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────

// ── 最新マウントへの委譲 ────────────────────────────────────────
// シナリオ生成 (mutation の onSuccess) とランダム生成のシェル別ポーリングは、投入したマウントが
// アンマウントされても (別画面へ遷移して戻る等) 非同期に進み続ける。そのとき古いクロージャが
// 持つ useState の setter は新しいマウントに届かない (phase / シーン設定等がロードされない)。
// そこで結果適用・タブ操作・自動生成後処理は、常に「今マウントされている最新インスタンス」の
// ハンドラへ委譲する。useScenarioGeneration は ScenarioGenerator 1 箇所でしか使われず、同時に
// 複数マウントされない前提 (ルート単位) なのでモジュールスコープで保持する。
// 復帰 effect 側は utils の in-flight registry を見て二重ポーリングしない (所有権は投入元の
// 非同期フローに置いたまま、結果の適用先だけを最新マウントへ移す)。
// マウントが 1 つも無いとき (別画面にいる間に完了) は古い setter へ流さず pendingEvents に
// 保留し、次回マウントの登録 effect で消費する。復帰用 sessionStorage キーは消費まで残す
// (リロードで保留が消えても復帰 effect がジョブ結果を取り直せる)。
type GenerateScenarioData = Awaited<ReturnType<typeof requestGenerateScenario>>;
/** シナリオ生成 mutation の variables。
 *  clientJobId (= track ID = 生成キューの行 ID) は呼び出し側 (handleSubmit) が採番して
 *  variables で渡す。ref 経由にすると「切り離し → 即再実行」で先発のコールバックが
 *  後発の ID を読む順序依存が生まれるため (計画書 §4.3)。payload だけを API へ送る。 */
export type ScenarioGenerateVariables = {
  payload: Record<string, unknown>;
  clientJobId: string;
  /** 全編モーション (映像スタイルがモーション・§10.5) のときだけ。生成前モーダルで選んだ BGM。
   *  あれば generate-async ではなく motion/prepare を呼び、シェル 1 本の早期遷移フローに乗せる。 */
  motionBgm?: MotionBgmChoice;
};

/**
 * 全編モーションの BGM 先行フロー (POST /scenarios/motion/prepare)。シェルを 1 本作って即遷移するので、
 * ランダム生成と同じシェルフローの形 (__shellFlow) で返す。シェルの本文待ちは pollShellScenario (シナリオ行の
 * status) が行い、BGM の確認画面は MotionPrepareStagePanel が出す。
 * リロード復帰用の sessionStorage キーには載せない: BGM 段階はシナリオ生成ジョブがまだ無く、再読み込み後は
 * シェルの行 (status='generating' + motion_prepare) から確認画面 / 待ちを復元するため。
 */
async function requestMotionPrepare(
  payload: Record<string, unknown>,
  bgm: MotionBgmChoice,
  opts?: { onJobCreated?: (jobId: string) => boolean | void },
): Promise<GenerateScenarioData> {
  const res = await prepareMotionScenario(payload, bgm);
  const serverJobId = res.scenario_job_id ?? res.bgm_job_id ?? res.scenario_id;
  opts?.onJobCreated?.(serverJobId);
  if (res.bgm_job_id) rememberMotionBgmJob(res.scenario_id, res.bgm_job_id);
  return {
    __shellFlow: true,
    jobId: serverJobId,
    shellScenarios: [{
      id: res.scenario_id,
      model: typeof payload.model === 'string' ? payload.model : '',
      motion: {
        bgmMode: bgm.mode,
        autoContinue: bgm.mode === 'generate' ? bgm.auto_continue : true,
        scenarioJobId: res.scenario_job_id ?? null,
        ...(res.song !== undefined ? { song: res.song } : {}),
      },
    }],
  };
}
type LatestInstanceDelegate = {
  onGenerationSuccess: (data: GenerateScenarioData, scenarioJobId?: string | null) => void;
  // ready になったシェルの本文ロード + 自動生成後処理 (focus 判定は最新マウントの
  // activeScenarioId で行う)。false = ロード失敗 (呼び出し側がリトライ)。
  // clientJobId は track の再判定用 (ロード中に切り離されたら反映も自動生成もしない)。
  handleReadyShell: (shellId: string, shellBatchId: string | undefined, clientJobId: string | null) => Promise<boolean>;
  removeShellTab: (id: string, model: string) => void;
  // 切り離し時のタブ除去 (失敗トーストを出さない removeShellTab の複数版)。
  detachShellTabs: (ids: string[]) => void;
};
const latestInstanceRef: { current: LatestInstanceDelegate | null } = { current: null };
// 保留イベントは track ID (clientJobId) を持つ: 積まれた後に切り離された生成のイベントを
// 消費時に捨てるため (別画面にいる間も生成キューの ✕ は押せる)。
export type PendingGenerationEvent =
  | { kind: 'success'; data: GenerateScenarioData; scenarioJobId?: string | null; clientJobId: string | null }
  | { kind: 'shellReady'; shellId: string; shellBatchId?: string; clientJobId: string | null }
  | { kind: 'shellRemove'; id: string; model: string; clientJobId: string | null };
const pendingGenerationEvents: PendingGenerationEvent[] = [];
// track の削除条件 (§4.1) 用: 当該 track の保留イベントが残っているか。
setPendingEventProbe((clientJobId) => pendingGenerationEvents.some((ev) => ev.clientJobId === clientJobId));
// 保留イベントの消費中 (非同期)。復帰 effect はこの間ジョブを取り直さない。
let pendingDrainInProgress = false;
// 進行中のシェルフロー (ランダム生成) の「表示復元用」記録。track とは別に、全シェル確定
// (settled) + 保留イベント消費まで保持する。型は scenarioGenerationTracks.ts に移設済み。
let inFlightShellFlow: InFlightShellFlow | null = null;
// シェル ready 後に自動生成後処理を発火済みのシナリオ ID (多重課金・多重生成を防ぐ
// idempotency キー)。委譲先のマウントが変わっても効くようモジュールスコープに置く。
const shellAutoGenFiredIds = new Set<string>();

// 生成キューの行 ID 兼 track ID。Date.now() だけだと「切り離し → 即再投入」のように
// 同一ミリ秒で 2 本投入されたときに衝突し、後発が先発の track を上書きしてしまう
// (= この計画が防ごうとしている混線そのもの) ため、単調カウンタを足す。
let scenarioClientJobSeq = 0;
function newScenarioClientJobId(): string {
  scenarioClientJobSeq += 1;
  return `scenario-${Date.now()}-${scenarioClientJobSeq}`;
}

// ── シェルフロー (ランダム生成) のポーリング ────────────────────
// attached / detached で共通に使えるよう、マウントに依存しない形でモジュールスコープに置く。
// detached (切り離し済み) のときは画面操作を一切せず、ready でシナリオ一覧の無効化だけ行う。

/** シェルのタブ除去。detached ではタブが既に無いので何もしない。 */
function makeRemoveShellTab(flow: InFlightShellFlow, clientJobId: string | null) {
  return (id: string, model: string) => {
    flow.removedIds.add(id);
    if (flow.detached) return;
    const target = latestInstanceRef.current;
    if (target) target.removeShellTab(id, model);
    else pendingGenerationEvents.push({ kind: 'shellRemove', id, model, clientJobId });
  };
}

type ShellPollContext = {
  flow: InFlightShellFlow;
  clientJobId: string | null;
  shellBatchId?: string;
  removeShellTab: (id: string, model: string) => void;
  /** detached の ready で呼ぶ (シナリオブック一覧の更新)。 */
  invalidateScenarioList: () => void;
};

/** 1 シェルぶんのポーリング。ready で本文ロード (attached) / 一覧無効化 (detached) を行う。 */
async function pollShellScenario(shell: ShellScenario, ctx: ShellPollContext): Promise<void> {
  const { flow } = ctx;
  const MAX_POLLS = 200;
  for (let i = 0; i < MAX_POLLS; i++) {
    await waitForNextPoll(3000);
    if (flow.loadedIds.has(shell.id)) return;
    let full: Awaited<ReturnType<typeof getScenario>>;
    try {
      full = await getScenario(shell.id);
    } catch (err) {
      // 404 = soft-delete (failed) → タブ除去。5xx/ネットワークはリトライ。
      if ((err as { response?: { status?: number } })?.response?.status === 404) {
        ctx.removeShellTab(shell.id, shell.model);
        return;
      }
      continue;
    }
    if (full.status === 'generating') {
      // 全編モーションの BGM 段階 (シナリオ生成ジョブ未投入 = BGM 生成中 / 利用者の確認待ち) は
      // 打ち切りの回数に数えない (確認画面で止まっている間にタブを消さないため・§10.5)
      const prep = readMotionPrepare(full as { generation_metadata?: Record<string, unknown> | null });
      if (prep && !prep.scenarioJobId && !flow.detached) i -= 1;
      continue;
    }
    if (full.status === 'failed') { ctx.removeShellTab(shell.id, shell.model); return; }
    // ready (status 未設定も ready 相当)。
    if (flow.loadedIds.has(shell.id)) return;
    // 切り離し済み: 作業エリアへは反映せず、シナリオブック一覧の更新だけ行う
    // (本文ロード・絵コンテ/ナレーション/フレームの自動生成はしない)。
    if (flow.detached) {
      flow.loadedIds.add(shell.id);
      ctx.invalidateScenarioList();
      return;
    }
    // 本文ロード + 自動生成後処理 (フレーム/ナレーション/絵コンテ) を最新マウントで行う。
    // focus はアクティブタブのときだけ true = 裏で完了したタブが表示中タブを奪わない
    // (判定は最新マウントの activeScenarioId で行う)。マウントが無ければ保留して
    // 「処理済み」扱いにする (次回マウントの登録 effect がロード + 後処理を行う)。
    const target = latestInstanceRef.current;
    let handled: boolean;
    if (target) {
      handled = await target.handleReadyShell(shell.id, ctx.shellBatchId, ctx.clientJobId);
    } else {
      pendingGenerationEvents.push({
        kind: 'shellReady', shellId: shell.id, shellBatchId: ctx.shellBatchId, clientJobId: ctx.clientJobId,
      });
      handled = true;
    }
    if (handled) {
      flow.loadedIds.add(shell.id);
      return;
    }
    // ロード失敗 → リトライ (DB 上は ready なので一時的なクライアント側失敗を想定)
  }
  ctx.removeShellTab(shell.id, shell.model);
}

/**
 * このタブのシェルフローがまだ本文待ちでポーリングしているシェルか。全編モーションの確認画面
 * (MotionPrepareStagePanel) が「ready になったら本文を読み込む」を、ポーリング側と二重に行わないために使う
 * (ポーリングしていない = 再読み込み後に行から復元したシェル は確認画面側が読み込む)。
 */
export function isShellPolledInSession(scenarioId: string): boolean {
  const flow = inFlightShellFlow;
  if (!flow || flow.detached) return false;
  return flow.placeholders.some((p) => p.id === scenarioId)
    && !flow.loadedIds.has(scenarioId)
    && !flow.removedIds.has(scenarioId);
}

/** 全シェルが ready/除去で出揃ったときの確定処理を作る (シェル 1 本の確定ごとに呼ぶ)。 */
function createSettleShell(params: {
  flow: InFlightShellFlow;
  serverJobId: string;
  clientJobId: string | null;
  shellCount: number;
  /** attached のみ: 生成キュー行の完了更新・絵コンテ集約バッチの close。 */
  onAllSettled?: () => void;
}): () => void {
  let pendingShells = params.shellCount;
  return () => {
    pendingShells -= 1;
    if (pendingShells > 0) return;
    // 全シェル確定。シェルフローは pollScenarioJob を通らないため、in-flight registry を
    // ここで解除する。requestGenerateScenario がジョブ投入時に積んだ「リロード復帰用」の
    // sessionStorage キーも、保留イベントが無ければここで消す (保留があれば次回マウントの
    // 消費後に消す)。消さないと次回 ScenarioGenerator のマウント時 (別画面から戻る等) に
    // 復帰 effect が完了済みジョブを通常生成の完了として再処理し、全シナリオぶんの
    // 絵コンテ / ナレーション自動生成が再発火する (本番で再現した多重生成の原因)。
    // 全シェル確定前は残す: リロードで途中離脱した場合の救済に復帰経路を使うため。
    settleScenarioJob(params.serverJobId);
    params.flow.settled = true;
    if (params.flow.detached) {
      // 切り離し済み: キュー行もバッチも切り離し時に処理済み。生成中判定から外すだけ。
      if (params.clientJobId) finishTrack(params.clientJobId);
      return;
    }
    // 保留イベント (別画面滞在中に ready になったシェル) が残っていれば、次回マウントの
    // 表示復元に使うので記録を保持する (消費後に登録 effect が破棄)。
    // 自フローのイベントだけを数えず全体の長さで見てよい理由: detached なフローは保留イベントを
    // 積まず、attached なシェルフローは同時に 1 本 (attached track は最大 1 件) なので、
    // ここに残り得る shellReady / shellRemove は自フローのものだけ。
    if (pendingGenerationEvents.length === 0) {
      if (inFlightShellFlow === params.flow) inFlightShellFlow = null;
      clearScenarioJobResumeKeyIfSettled();
    }
    if (params.clientJobId) finishTrack(params.clientJobId);
    params.onAllSettled?.();
  };
}

/**
 * 別画面滞在中に積まれた保留イベントを、最新マウントのハンドラで消費する。
 * (呼び出し側が `pendingGenerationEvents.splice(0)` で取り出した配列を渡す)
 *
 * 切り離された生成のイベントは捨てる: 積まれた後でも生成キューの ✕ は押せるため、
 * そのままロード・自動生成すると消したタブが復活し、ポイントも使ってしまう (§4.5)。
 */
export async function drainPendingGenerationEvents(
  events: PendingGenerationEvent[],
  own: LatestInstanceDelegate,
  deps: {
    invalidateScenarioList: () => void;
    handleDetachedGenerationOutcome: (track: ScenarioGenerationTrack, data: GenerateScenarioData) => void;
  },
): Promise<void> {
  // splice で保留キューから出た瞬間に、取り出した全件を track の「処理待ち」として
  // 計上する (同一 track の 2 件目以降を処理し終えるまで track を消さないため。§4.5)。
  // 最初の await より前に同期的に行う。
  for (const ev of events) {
    if (ev.clientJobId) beginHandler(ev.clientJobId);
  }
  for (const ev of events) {
    try {
      // 積まれた後に切り離された生成のイベントは捨てる (ロード・自動生成をしない)。
      const track = ev.clientJobId ? getTrack(ev.clientJobId) : undefined;
      if (track?.detached) {
        if (ev.kind === 'success') {
          deps.handleDetachedGenerationOutcome(track, ev.data);
        } else if (ev.kind === 'shellReady') {
          // 本文ロードはしないが、シナリオブック一覧だけ更新する。
          deps.invalidateScenarioList();
        }
        continue;
      }
      if (ev.kind === 'success') {
        own.onGenerationSuccess(ev.data, ev.scenarioJobId);
      } else if (ev.kind === 'shellRemove') {
        own.removeShellTab(ev.id, ev.model);
      } else {
        // 本文ロードは一時的に失敗しうる (素材復元等) ので数回リトライする。
        let handled = false;
        for (let attempt = 0; attempt < 5 && !handled; attempt++) {
          if (attempt > 0) await waitForNextPoll(3000);
          // リトライ待ちの間に切り離されることがあるので毎回再判定する。
          if (ev.clientJobId && getTrack(ev.clientJobId)?.detached) {
            deps.invalidateScenarioList();
            handled = true;
            break;
          }
          handled = await own.handleReadyShell(ev.shellId, ev.shellBatchId, ev.clientJobId);
        }
        if (!handled) console.error('[ScenarioGenerator] Failed to load ready shell after resume:', ev.shellId);
      }
    } catch (error) {
      // 1 件の失敗で残りのイベントを落とさない (endHandler も finally で必ず走る)。
      console.error('[ScenarioGenerator] Failed to consume pending generation event:', ev.kind, error);
    } finally {
      // このイベントぶんの処理待ちを解消する (破棄・失敗打ち切りも含む)。
      // ここで track の解放条件 (finished && inFlight===0 && 保留イベント無し) も再評価される。
      if (ev.clientJobId) endHandler(ev.clientJobId);
    }
  }
}

/** 完了後の後書き (frame_mode / 参考画像一覧) を DB へ書く (fire-and-forget)。
 *  マウント不在でも実行できるよう、マウントローカルの useMutation ではなく
 *  API クライアント (features/scenarios/api の updateScenario) を直接呼ぶ純粋関数にしてある (§4.5)。 */
export function postWriteScenarioFields(
  scenarioIds: string[],
  postWrite: ScenarioPostWrite,
  onWritten?: (scenarioId: string) => void,
): void {
  const payload: Record<string, unknown> = { frame_mode: postWrite.frameMode };
  if (postWrite.referenceImageUrls) payload.reference_image_urls = postWrite.referenceImageUrls;
  scenarioIds.forEach((scenarioId) => {
    void updateScenario(scenarioId, payload)
      .then(() => { onWritten?.(scenarioId); })
      .catch((error) => {
        console.warn('[ScenarioGenerator] Failed to post-write scenario fields:', scenarioId, error);
      });
  });
}

/** 切り離した通常フローの完了処理 (縮退モード)。
 *  作業エリアへの反映・自動生成・トースト・復帰キー操作は一切行わない (§4.5)。 */
export function applyDetachedGenerationResult(
  track: ScenarioGenerationTrack,
  data: GenerateScenarioData,
  deps: {
    postWriteScenarioFields: (scenarioIds: string[], postWrite: ScenarioPostWrite) => void;
    invalidateScenarioList: () => void;
  },
): void {
  // シェル応答はポーリングがこれから始まるので、この関数の対象外 (呼び出し側で分岐する)。
  if (isShellFlowOutcome(data)) return;
  if (data.created !== true) {
    console.warn('[ScenarioGenerator] 切り離した生成の保存に失敗しました:', track.clientJobId);
    return;
  }
  const scenarioIds = (data.scenarios ?? []).map((s) => s.id).filter(Boolean);
  if (scenarioIds.length > 0) {
    // 値は生成開始時に控えたもの。完了時点の画面の設定は切り離し後に変わっている可能性がある。
    deps.postWriteScenarioFields(scenarioIds, track.postWrite);
  }
  deps.invalidateScenarioList();
}

/** テスト専用: このモジュールが持つ進行中フローの記録を初期化する。
 *  (テスト間で前のテストの保留イベント・シェルフロー記録が残ると、次のテストの
 *  マウント effect が消費・復元を始めてしまうため) */
export function __resetScenarioGenerationStateForTest(): void {
  inFlightShellFlow = null;
  pendingGenerationEvents.length = 0;
  pendingDrainInProgress = false;
  latestInstanceRef.current = null;
  shellAutoGenFiredIds.clear();
}

export function useScenarioGeneration(params: UseScenarioGenerationParams) {
  // partner (OEM) が UI 非表示にしている項目は、localStorage に過去の選択値が
  // 残っていても payload に載せない。backend のデフォルト挙動 (= 注記なし) に倒す。
  const _scenarioFieldConfig = useScenarioFieldConfig();
  const _effIncludeNarration = (inc: string) =>
    _scenarioFieldConfig.show_narration ? inc : 'unspecified';
  const _effIncludeDialogue = (inc: string) =>
    _scenarioFieldConfig.show_dialogue ? inc : 'unspecified';
  const _effIncludeTelop = (inc: string) =>
    _scenarioFieldConfig.show_telop ? inc : 'unspecified';

  const {
    scenarioIdFromPath,
    currentUserId,
    lastResult,
    setLastResult,
    mode,
    setMode,
    videoGenerationMode,
    setVideoGenerationModeTransient,
    setExpandedVideos,
    setExpandedNarrations,
    setIsProductSectionOpen,
    setSceneVideoSettings,
    setRecommendedNarrator,
    tempMaterials,
    productSelectRef,
    setShowProductTooltip,
    handleBatchGenerateFirstFrames,
    autoGenerateStoryboards,
    isMotionEngine = false,
    isLyricsTone = false,
    isMotionDurationTooLong = false,
    requestMotionBgmChoice,
    importedScript = null,
  } = params;

  // 商品入力フォーム (useProductSelection)
  const {
    asin,
    setAsin,
    manualProductId,
    setManualProductId,
    setManualProductTitle,
    setManualProductImageUrl,
    productName,
    setProductName: _setProductName,
    brandName,
    setBrandName: _setBrandName,
    sellingPoints,
    setSellingPoints,
    goal,
    setGoal,
    targetAudience,
    setTargetAudience,
    pricePoint,
    setPricePoint: _setPricePoint,
    setManualProductPromptDismissed,
    setFetchedAsins,
    setFetchedProductIds,
  } = params.product;

  // シナリオ設定フォーム (useScenarioFormState)
  const {
    tone,
    setTone: _setTone,
    duration,
    characterCount,
    additionalPrompt,
    scenarioCount,
    scenarioModel,
    lastFrameMode,
    includeNarration,
    includeTelop,
    includeDialogue,
    autoGenerateFirstFrame,
    autoGenerateStoryboard,
    excludePeople,
  } = params.form;

  // 生成ステート (useGenerationState)。以前は親から props で受け取っていたが、
  // 同じ Context を直接読むだけなので params から外した。
  const {
    selectedAssets,
    selectedCasting,
    setSelectedCasting,
    additionalCastings,
    setAdditionalCastings,
    selectedSeedancePresetActor,
    setSelectedSeedancePresetActor,
    additionalSeedancePresetActors,
    setAdditionalSeedancePresetActors,
    activeScenarioId,
    setActiveScenarioId,
    setActiveJobs,
    migrateAssetsToScopes,
    addAssetToScope,
    removeAssetFromScope,
    replaceAssetsInScope,
    clearAssets,
    clearAllScopedAssets,
    setScenarioInputTab,
    videoSettings,
    setVideoSettings,
    avoidFaces,
    visualStyle,
    shootingMethod,
    setShootingMethod,
    dialect,
    setDialect,
    motionGraphics,
    setMotionGraphics,
    motionGraphicsOptions,
    targetGender,
    setTargetGender,
    targetAge,
    setTargetAge,
    setVisualStyle,
    visualStyleIsStylized,
    narrationTreeEnabled,
    narrationCategoryEnabled,
    dialogueTreeEnabled,
    dialogueCategoryEnabled,
    telopTreeEnabled,
    telopCategoryEnabled,
    openStoryboardBatch,
    closeStoryboardBatch,
    discardStoryboardBatch,
  } = useGenerationState();

  // ナレ/セリフ/テロップの payload 断片(フェーズ6)。フラグ ON のカテゴリは option key を
  // フィールド名(narration/dialogue/telop)で送る(backend が tree 解決)。フラグ OFF は従来の
  // include_* 真偽値。除外/非表示は無送信(=指定なし)。3つの mutate 呼び出しで共用。
  const _behaviorPayloadFields = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    // narration: tree 有効(フラグ ON かつ非除外)は key をフィールド名で送る(表示ゲートはツリー
    // 可視性が正本のため旧 show_* と AND しない)。フラグ OFF は legacy 値を**明示マップ**する
    // (include/exclude のみ真偽。残存 tree key 'narration_on' 等は undefined=指定なしに落とす)。
    if (narrationCategoryEnabled) {
      if (narrationTreeEnabled && includeNarration && includeNarration !== 'unspecified') {
        out.narration = includeNarration;
      }
    } else {
      const v = _effIncludeNarration(includeNarration);
      out.include_narration = v === 'include' ? true : v === 'exclude' ? false : undefined;
    }
    // dialogue
    if (dialogueCategoryEnabled) {
      if (dialogueTreeEnabled && includeDialogue && includeDialogue !== 'unspecified') {
        out.dialogue = includeDialogue;
      }
    } else {
      const v = _effIncludeDialogue(includeDialogue);
      out.include_dialogue = v === 'include' ? true : v === 'exclude' ? false : undefined;
    }
    // telop (legacy は include/generate_only=true, exclude=false, それ以外=指定なし)
    if (telopCategoryEnabled) {
      if (telopTreeEnabled && includeTelop && includeTelop !== 'unspecified') {
        out.telop = includeTelop;
      }
    } else {
      const v = _effIncludeTelop(includeTelop);
      out.include_telop = (v === 'include' || v === 'generate_only') ? true : v === 'exclude' ? false : undefined;
      out.telop_generate_only = v === 'generate_only' || undefined;
    }
    return out;
  };

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // シーン既定尺の均等割りをモデル対応尺 (generation_models.supported_durations) でクランプする
  // ため。未取得の間は computeEvenSplitDefaultDuration が上限を掛けない (SceneItem の範囲外
  // スナップがモデル解決後に下方修正する)。react-query のキャッシュ共有なので追加リクエストは出ない。
  const { data: videoModelsForDuration = [] } = useGenerationModels('video');
  const supportedDurationsOf = (modelCode: string): number[] | null =>
    videoModelsForDuration.find((m) => m.model_code === modelCode)?.supported_durations ?? null;
  const toast = useToast();
  const queryClient = useQueryClient();
  // 切り離した生成の完了はサイレント (トースト・導線なし)。シナリオブック一覧を開いていれば
  // 更新されるよう、クエリの無効化だけ行う。queryClient はアプリ単位のシングルトンなので
  // マウントが無くなった後のコールバックから呼んでも問題ない。
  const invalidateScenarioList = () => {
    queryClient.invalidateQueries({ queryKey: ['scenarios'] });
  };

  // 非同期 fetch (fetchMaterialById / getProduct / saveImageToTemporary 等) が完了したときに
  // 「同シナリオの後続ロード」で上書きされた状態に対して古い結果を流し込まないための
  // scenarioId ごとの世代トークン。
  // 単一カウンタにすると `handleLoadScenarios` で複数シナリオを順次ロードしたとき、
  // 後続シナリオのトークン増加が前シナリオの in-flight async chain を誤キャンセルしてしまう
  // ため、scope (= scenarioId) 単位で管理する。
  const loadTokensRef = useRef<Map<string, number>>(new Map());
  const issueLoadToken = useCallback((scenarioId: string): number => {
    const next = (loadTokensRef.current.get(scenarioId) ?? 0) + 1;
    loadTokensRef.current.set(scenarioId, next);
    return next;
  }, []);
  const isLoadTokenCurrent = useCallback((scenarioId: string, token: number): boolean => {
    return loadTokensRef.current.get(scenarioId) === token;
  }, []);

  // シナリオ生成直後の navigate でも URL effect が走るが、その時点で lastResult の
  // commit が React Router の URL 反映より遅れて handleLoadScenario が誤発火する race
  // が確認されている (storyboard モードで「外した素材が 1 枚ずつ復活」報告)。
  // mutation.onSuccess で navigate より前に scenario.id を ref に積み、URL effect は
  // この ref を見て handleLoadScenario を skip する。
  const recentlyCreatedScenarioIdsRef = useRef<Set<string>>(new Set());
  // ランダム生成は生成完了前に skeleton シナリオを表示するため、送信時点の人物指定を
  // placeholder に載せる。これが無いと生成中だけ selectedAssetsSticky / cap 復元が単数扱いになる。
  const shellGenerationSnapshotRef = useRef<ShellGenerationSnapshot | null>(null);

  // ランダム生成のシェルポーリング (長時間 async) で、stale クロージャを避けて最新の
  // lastResult / activeScenarioId を読むためのミラー ref。
  const lastResultRef = useRef(lastResult);
  useEffect(() => { lastResultRef.current = lastResult; }, [lastResult]);
  const activeScenarioIdRef = useRef(activeScenarioId);
  useEffect(() => { activeScenarioIdRef.current = activeScenarioId; }, [activeScenarioId]);
  // このインスタンスのハンドラ束。毎 render で最新のハンドラに差し替え、マウント中は
  // latestInstanceRef に登録する (in-flight のフローはこれ経由で最新マウントを呼ぶ)。
  // このマウントで表示を復元済みのシェルフロー記録 (StrictMode の effect 二重実行で二重復元しない)。
  const shellFlowRestoredRef = useRef<InFlightShellFlow | null>(null);
  const ownDelegateRef = useRef<LatestInstanceDelegate>({
    onGenerationSuccess: () => {},
    handleReadyShell: async () => false,
    removeShellTab: () => {},
    detachShellTabs: () => {},
  });
  useEffect(() => {
    const own = ownDelegateRef.current;
    latestInstanceRef.current = own;
    // シェルフロー進行中 (または確定済みだが保留イベント未消費) の再マウント: スケルトンタブ
    // (lastResult) と phase はマウントローカルなので消えている。記録から復元し、ready 済みの
    // 本文はこのマウントへ取り直す。保留イベントの消費 (下) より先に行い、消費時の focus 判定
    // が復元後のアクティブタブを見るようにする。
    if (inFlightShellFlow && !lastResultRef.current && shellFlowRestoredRef.current !== inFlightShellFlow) {
      shellFlowRestoredRef.current = inFlightShellFlow;
      restoreInFlightShellFlow(inFlightShellFlow);
    }
    // 別画面にいる間に完了・確定したイベントを、このマウントのハンドラで消費する。
    if (pendingGenerationEvents.length > 0) {
      const events = pendingGenerationEvents.splice(0);
      pendingDrainInProgress = true;
      void drainPendingGenerationEvents(events, own, {
        invalidateScenarioList,
        handleDetachedGenerationOutcome,
      }).finally(() => {
        pendingDrainInProgress = false;
        // 全シェル確定済みの記録は、保留イベントを消費し終えたので破棄する。
        if (inFlightShellFlow?.settled && pendingGenerationEvents.length === 0) inFlightShellFlow = null;
        // 消費したジョブが追跡終了済みなら復帰用キーを消す (まだシェルが動いていれば残す)。
        clearScenarioJobResumeKeyIfSettled();
      });
    }
    return () => {
      if (latestInstanceRef.current === own) latestInstanceRef.current = null;
    };
  // マウント時 1 回だけ登録・復元・消費する (restoreInFlightShellFlow は render ごとの関数)。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    setScenePreviews, setScenePreviewIndex, setScenePreviewMaterials,
    setSceneVideoGenerated, setSceneVideoMaterialId,
    setConfirmedFirstFrames, setSceneLastFrames,
    clearAll: clearAllSceneState,
  } = useSceneStateContext();

  // ── State managed by this hook ──────────────────────────────
  // URL に scenarioId が含まれている場合は、初期表示でセットアップ画面を一瞬出さず
  // 直接 work フェーズから始めてオーバーレイで読み込みを示す
  const [phase, setPhase] = useState<'setup' | 'work'>(
    scenarioIdFromPath || searchParams.get('scenario_id') ? 'work' : 'setup'
  );
  const [hasTransitioned, setHasTransitioned] = useState(false);
  // 並列・連続呼び出しを正しく扱うためカウンタで管理し、boolean を派生させる
  const [loadingScenarioCount, setLoadingScenarioCount] = useState(0);
  const isLoadingScenario = loadingScenarioCount > 0;

  const changePhase = useCallback((newPhase: 'setup' | 'work') => {
    setHasTransitioned(true);
    setPhase(newPhase);
  }, []);

  // ── Scenario generation mutation ────────────────────────────
  // 最後に失敗した「切り離した生成」の clientJobId。detached の失敗はサイレントなので (§2)、
  // mutation observer が**その生成のエラーを表示している**ときだけ isError / error を落とす。
  // 真偽値ではなく ID で持つのは、後発 (attached) の失敗バナーを誤って潰さないため
  // (A を切り離す → B を投入 → A が失敗、の順でも observer は B を向いている)。
  const detachedErrorJobIdRef = useRef<string | null>(null);
  // 完了後の後書き (frame_mode / 参考画像一覧) に使う値を、生成開始時点で控える。
  // 切り離した後にユーザーが設定を変えても、古いシナリオには開始時の値を書く (§4.3)。
  const captureScenarioPostWrite = (): ScenarioPostWrite => {
    const frameMode = videoGenerationMode === 'reference'
      ? 'reference'
      : videoGenerationMode === 'storyboard'
        ? 'storyboard'
        : lastFrameMode;
    if (videoGenerationMode !== 'reference') return { frameMode };
    return {
      frameMode,
      referenceImageUrls: selectedAssets
        .filter((asset) => (asset.usage ?? 'reference') === 'reference')
        .map((asset) => ({ id: asset.id, sourceUrl: asset.sourceUrl || '', thumbnailUrl: asset.thumbnailUrl || '' })),
    };
  };
  const handleScenarioGenerationSuccess = (data: Awaited<ReturnType<typeof requestGenerateScenario>>, scenarioJobId?: string | null) => {
    // 絵コンテ自動生成バッチ。通常フローで絵コンテ自動生成する場合、シナリオ完了通知を
    // 抑止し、全絵コンテ完了時に集約通知する (後段の autoGenerateStoryboards で使う)。
    let normalBatchId: string | undefined;
    if (scenarioJobId && !isShellFlowOutcome(data)) {
      if (data.created !== true) {
        // DB 永続化に失敗したケース（下流の早期エラーと同じ条件）。完了通知を出さず
        // 失敗として扱う。
        setActiveJobs((prev) =>
          prev.map((j) =>
            j.jobId === scenarioJobId
              ? { ...j, status: 'failed' as const, error: 'シナリオの保存に失敗しました' }
              : j,
          ),
        );
      } else {
        if (autoGenerateStoryboard && videoGenerationMode === 'storyboard' && (data.scenarios?.length ?? 0) > 0) {
          normalBatchId = openStoryboardBatch();
        }
        setActiveJobs((prev) =>
          prev.map((j) =>
            j.jobId === scenarioJobId
              ? { ...j, status: 'completed' as const, ...(normalBatchId ? { notifyBatchId: normalBatchId } : {}) }
              : j,
          ),
        );
      }
    }

    // ── ランダム生成 (シェル) の早期遷移フロー ───────────────────────────
    // ジョブ完了を待たず、shell 情報からスケルトンタブを描画して即遷移し、
    // 各シェルを個別ポーリングして ready で本文差し替え / failed でタブ除去する。
    if (isShellFlowOutcome(data)) {
      const { shellScenarios } = data;
      // 絵コンテ自動生成バッチ。シェルはモードがシェルごとに異なりうるため、
      // チェック ON なら先にバッチを開いてシナリオ完了通知を抑止し、ready になった
      // storyboard モードのシェルから順にスロットを足す。全シェル出揃い + 全絵コンテ
      // 完了で 1 本だけ通知する (storyboard シェルが 0 件なら total===0 でシナリオ
      // 完了通知へフォールバック)。
      const shellBatchId = autoGenerateStoryboard ? openStoryboardBatch() : undefined;
      const shellSnapshot = shellGenerationSnapshotRef.current;
      const shellGenerationMetadata = shellSnapshot?.characterCountKey || shellSnapshot?.characterCount
        ? {
            character_count_key: shellSnapshot.characterCountKey,
            character_count: shellSnapshot.characterCount,
          }
        : undefined;
      const placeholders: Scenario[] = shellScenarios.map((s) => ({
        id: s.id,
        title: s.motion
          ? (s.motion.bgmMode === 'generate' && !s.motion.scenarioJobId ? '（モーション: BGM を準備中…）' : '（モーション: 台本を生成中…）')
          : `（${scenarioModelDisplayName(s.model)}で生成中…）`,
        summary: '',
        status: 'generating',
        // シェルの作成者 = 現在のユーザー。ready 後は詳細読み込みで上書きされるが、
        // 生成中に情報モーダルを開いても自分のシナリオとして扱えるよう先に入れておく (計画 §3.4.1)
        user_id: currentUserId,
        aspect_ratio: videoSettings.aspectRatio,
        video_model: videoSettings.videoModel,
        output_resolution: videoSettings.resolution,
        // 全編モーションのシェルは backend のシェルと同じ印 (production_engine / motion_prepare) を持たせ、
        // WorkPanel が BGM の確認画面 (MotionPrepareStagePanel) を出せるようにする
        generation_metadata: s.motion
          ? {
              ...(shellGenerationMetadata ?? {}),
              production_engine: 'motion',
              production_mode: 'per_scene',
              motion_prepare: {
                bgm_mode: s.motion.bgmMode,
                auto_continue: s.motion.autoContinue,
                ...(s.motion.scenarioJobId ? { scenario_job_id: s.motion.scenarioJobId } : {}),
                ...(s.motion.song !== undefined ? { song: s.motion.song } : {}),
              },
            }
          : shellGenerationMetadata,
        character_count: shellSnapshot?.characterCount,
        casting_model_ids: shellSnapshot?.castingModels?.map((c) => c.id),
        casting_models: shellSnapshot?.castingModels,
        casting: shellSnapshot?.castingModels?.[0],
        seedance_preset_actor_ids: shellSnapshot?.seedancePresetActors?.map((a) => a.id),
        seedance_preset_actors: shellSnapshot?.seedancePresetActors,
        seedance_preset_actor: shellSnapshot?.seedancePresetActors?.[0],
        scenes: [],
      }));
      if (shellSnapshot?.castingModels?.length) {
        const [primary, ...additional] = shellSnapshot.castingModels;
        setSelectedCasting(primary);
        setAdditionalCastings(additional);
      }
      if (shellSnapshot?.seedancePresetActors?.length) {
        const [primary, ...additional] = shellSnapshot.seedancePresetActors;
        setSelectedSeedancePresetActor(primary);
        setAdditionalSeedancePresetActors(additional);
      }
      const shellResultMeta: Omit<ScenarioResponse, 'scenarios'> = {
        source_type: mode === 'prompt' ? 'custom' : mode,
        requested_scenarios: placeholders.length,
        created: true,
      };
      setLastResult({ ...shellResultMeta, scenarios: placeholders });
      const flow: InFlightShellFlow = {
        jobId: data.jobId,
        resultMeta: shellResultMeta,
        placeholders,
        removedIds: new Set(),
        loadedIds: new Set(),
        settled: false,
        detached: false,
      };
      inFlightShellFlow = flow;
      // この生成の track にシェルフローを結び付け、切り離し時の処理 (onDetach) を登録する。
      // 生成キューの ✕ はこの track 経由で作業エリアの後始末を呼ぶ。
      const shellTrack = scenarioJobId ? getTrack(scenarioJobId) : undefined;
      if (shellTrack) {
        shellTrack.shellFlow = flow;
        shellTrack.serverJobId = data.jobId;
        shellTrack.onDetach = () => {
          flow.detached = true;
          // 再マウント時にスケルトンタブを復元しない (タブは下で外す)。
          if (inFlightShellFlow === flow) inFlightShellFlow = null;
          const target = latestInstanceRef.current;
          if (target) target.detachShellTabs(flow.placeholders.map((p) => p.id));
          // 絵コンテ集約バッチ: スロットが 0 件のまま close すると「シナリオ生成完了」通知が
          // 切り離し時点で誤発火するため、0 件なら破棄・1 件以上なら通常どおり close する。
          if (shellBatchId) discardStoryboardBatch(shellBatchId);
        };
      }
      // URL effect が generating シェルを誤ってロードしないよう ref に積む。
      shellScenarios.forEach((s) => {
        recentlyCreatedScenarioIdsRef.current.add(s.id);
        window.setTimeout(() => recentlyCreatedScenarioIdsRef.current.delete(s.id), 5000);
      });
      setActiveScenarioId(shellScenarios[0].id);
      navigate(`/studio/video/${shellScenarios[0].id}`, { replace: false });
      changePhase('work');
      window.scrollTo(0, 0);

      // タブ除去・本文ロード・自動生成後処理は latestInstanceRef 経由で「最新マウント」の
      // ハンドラを呼ぶ (このクロージャは別画面へ遷移しても走り続けるため)。マウントが無ければ
      // 保留して次回マウントで消費する (冒頭のモジュールコメント参照)。
      // 全編モーションの BGM 先行フロー (シェル 1 本): 生成キューの行にシナリオ id を紐づける。
      // 確認画面 (MotionPrepareStagePanel) が BGM 完了後の操作待ちで行を paused にし、ステータスバー・
      // キュー・タブの生成中表示から外すため (利用者指示 2026-10-05)
      if (scenarioJobId && shellScenarios.length === 1 && shellScenarios[0].motion) {
        const shellId = shellScenarios[0].id;
        setActiveJobs((prev) => prev.map((j) => (j.jobId === scenarioJobId ? { ...j, scenarioId: shellId } : j)));
      }
      const removeShellTab = makeRemoveShellTab(flow, scenarioJobId ?? null);
      // 全シェルが ready/除去で出揃ったら、生成キューのシナリオ本体ジョブを完了にする
      // (= ステータスバーが消え、別タブ・最小化中なら完了通知が出る)。
      const settleShell = createSettleShell({
        flow,
        serverJobId: data.jobId,
        clientJobId: scenarioJobId ?? null,
        shellCount: shellScenarios.length,
        onAllSettled: () => {
          if (!scenarioJobId) return;
          setActiveJobs((prev) =>
            prev.map((j) =>
              j.jobId === scenarioJobId
                ? { ...j, status: 'completed' as const, ...(shellBatchId ? { notifyBatchId: shellBatchId } : {}) }
                : j,
            ),
          );
          // 全シェル確定 → これ以上スロットは増えない。バッチを閉じる
          // (絵コンテジョブが全確定したら集約通知、0 件ならシナリオ完了通知へフォールバック)。
          if (shellBatchId) closeStoryboardBatch(shellBatchId);
        },
      });
      const shellPollContext = {
        flow,
        clientJobId: scenarioJobId ?? null,
        shellBatchId,
        removeShellTab,
        invalidateScenarioList,
      };
      shellScenarios.forEach((s) => { void pollShellScenario(s, shellPollContext).then(settleShell, settleShell); });
      return;
    }

    // 結果をこのマウントで消費した = リロード復帰用キーはもう不要 (ジョブは pollScenarioJob
    // で追跡終了済み)。ここまで残しておくのは、アンマウント中に完了した結果を保留→次回
    // マウントで消費する間にリロードされても取り直せるようにするため。
    clearScenarioJobResumeKeyIfSettled();
    // バックエンドが DB 永続化に失敗していた場合、scenarios[].id は placeholder
    // ("scenario-1" 等) のままで返ってくる。このまま続行すると後続の
    // updateScenarioMutation / navigate / 各種 set... が連鎖的に壊れるため、
    // ここで早期にエラー扱いにする。
    if (data.created !== true) {
      toast.error('シナリオの保存に失敗しました。時間をおいて再度お試しください。');
      return;
    }
    setLastResult(data);
    // Gemini推奨ナレーター情報をフィルタに反映
    if (data.recommended_narrator) {
      setRecommendedNarrator(data.recommended_narrator);
    }
    // 先頭のシナリオを選択状態にしてURLを更新（素材を全シナリオのスコープに引き継ぐ）
    if (data.scenarios && data.scenarios.length > 0) {
      // attached の後書き値は従来どおり「完了時点のライブ値」(全シナリオで同じ)。
      const postWrite = captureScenarioPostWrite();
      // 生成時のアスペクト比とframe_modeを各シナリオに付与
      data.scenarios.forEach(s => {
        if (!s.aspect_ratio) {
          s.aspect_ratio = videoSettings.aspectRatio;
        }
        if (!s.video_model) {
          s.video_model = videoSettings.videoModel;
        }
        if (!s.output_resolution) {
          s.output_resolution = videoSettings.resolution;
        }
        s.frame_mode = postWrite.frameMode;
        if (postWrite.referenceImageUrls) {
          s.reference_image_urls = postWrite.referenceImageUrls;
        }
      });
      // DBにframe_mode（+ 参照画像モードの場合は参照画像URL）を保存（fire-and-forget）。
      // 切り離した生成 (マウント不在) と同じ書き込み関数を使う (§4.5)。
      postWriteScenarioFields(data.scenarios.map(s => s.id), postWrite, () => {
        // useUpdateScenario の onSuccess 相当 (['scenarios'] は prefix 一致で
        // ['scenarios', id] も含むので 1 回でよい)。
        invalidateScenarioList();
      });
      migrateAssetsToScopes(data.scenarios.map(s => s.id));
      // navigate より前に新シナリオ ID を記録: URL effect が race で先に発火しても
      // handleLoadScenario が誤って呼ばれないようにする。
      // React Strict Mode (dev) で URL effect が二重発火することがあるので、
      // 「使ったら delete」ではなく、setTimeout で遅延クリーンアップする。
      // 5 秒もあれば Strict Mode の二重 + その後の通常マウントは全て吸収できる。
      data.scenarios.forEach(s => {
        recentlyCreatedScenarioIdsRef.current.add(s.id);
        window.setTimeout(() => {
          recentlyCreatedScenarioIdsRef.current.delete(s.id);
        }, 5000);
      });
      setActiveScenarioId(data.scenarios[0].id);
      navigate(`/studio/video/${data.scenarios[0].id}`, { replace: false });
    }
    // ランダム生成で達成可能数に届かなかった場合は、正直に部分成功を伝える。
    // (予算都合の抽選除外・API失敗の両方を random_partial が反映している)
    if (data.random_partial) {
      const produced = data.scenarios.length;
      const requested = data.random_requested_count ?? produced;
      toast.success(`${produced}/${requested}案を生成しました（ポイントまたはモデルの都合で一部はスキップされました）`);
    } else {
      toast.success('シナリオ案を生成しました');
    }
    changePhase('work');
    window.scrollTo(0, 0);

    // 商品を含めるトグルの初期値を設定
    if (data.scenarios) {
      const initialLastFrames: Record<string, { url: string; sourceUrl?: string; materialId: string } | null> = {};
      data.scenarios.forEach(scenario => {
        scenario.scenes.forEach(scene => {
          const key = `${scenario.id}-scene-${scene.order}`;
          if (scene.last_frame_url) {
            initialLastFrames[key] = {
              url: scene.last_frame_url,
              sourceUrl: (scene as any).last_frame_source_url || scene.last_frame_url,
              materialId: scene.last_frame_material_id || '',
            };
          }
        });
      });
      setSceneLastFrames(initialLastFrames);


      // 音声生成は常時オン (下記 generateAudio。トグルは 2026-09-14 に撤廃)。
      // シーン既定尺はロード経路 (handleLoadScenario) と同じ優先順位で決める
      // (docs/requirements/per-scene-default-duration-fix-plan.md §3):
      //   1. レスポンスの scene.video_duration_seconds (frame / reference で AI が配分し
      //      バックエンドがモデル対応尺にクランプ済み)
      //   2. ストーリーボード: コマ尺合計 (想定尺は使わない)
      //   3. frame / reference で保存尺が無いとき: 想定尺 ÷ シーン数 (連続尺モデルのみ) → 4 秒
      // ⚠️ 想定尺の秒数はフォーム値 `duration` から取らない。属性ツリー有効時のフォーム値は
      // option key (`do_72d8…`) で、数字抽出すると 16 進 ID の数字を秒数と誤解釈する。
      // バックエンドが解決したラベル (generation_metadata.duration = "30秒") を使う。
      const isStoryboardModeNew = videoGenerationMode === 'storyboard';
      const initialVideoModel = resolveUsableVideoModel(videoSettings.videoModel);
      const initialResolution = videoSettings.resolution || '1080';
      const initialSceneVideoSettings: Record<string, { duration: number; generateAudio: boolean; videoModel?: string; resolution?: string }> = {};
      data.scenarios.forEach(scenario => {
        const sceneCount = scenario.scenes.length;
        const scenarioMeta = scenario.generation_metadata as Record<string, unknown> | undefined;
        const intendedTotalSeconds = parseIntendedDurationSeconds(
          typeof scenarioMeta?.duration === 'string' ? scenarioMeta.duration : null,
        );
        const storyboardTotalDuration = computeStoryboardTotalDuration(scenario.scenes);
        const evenSplitDefault = computeEvenSplitDefaultDuration(
          initialVideoModel,
          intendedTotalSeconds,
          sceneCount,
          supportedDurationsOf(initialVideoModel),
        );
        scenario.scenes.forEach(scene => {
          const key = `${scenario.id}-scene-${scene.order}`;
          initialSceneVideoSettings[key] = {
            duration: resolvePerSceneDefaultDuration({
              isStoryboardMode: isStoryboardModeNew,
              storedDuration: scene.video_duration_seconds,
              hasGeneratedVideo: !!scene.video_material_id,
              storyboardTotalDuration,
              evenSplitDefault,
            }),
            generateAudio: true,
            videoModel: initialVideoModel,
            resolution: initialResolution,
          };
        });
      });
      setSceneVideoSettings(initialSceneVideoSettings);
    }

    // 最初のフレームを自動生成（全シナリオを並列で、各シナリオ内はシーン順に逐次処理）
    // フレームモードのときのみ。参照画像モード・ストーリーボードモードではスキップ
    // (チェックボックスは非表示だが state は残存し得る)。
    if (autoGenerateFirstFrame && videoGenerationMode !== 'reference' && videoGenerationMode !== 'storyboard' && data.scenarios) {
      data.scenarios.forEach((scenario, index) => {
        setTimeout(() => {
          handleBatchGenerateFirstFrames({ scenarios: data.scenarios, scenarioId: scenario.id });
        }, index * 2000);
      });
    }
    // ストーリーボードを自動生成: ストーリーボードモードのときのみ。
    // 各シナリオを個別にジョブ投入するため、シナリオ別キューで進行する。
    if (autoGenerateStoryboard && videoGenerationMode === 'storyboard' && data.scenarios) {
      // batchId 付きで投入 (forEach 内で同期的にスロット登録される) → 直後に close。
      // 全シナリオの全絵コンテ完了時に集約通知が 1 本だけ出る。
      autoGenerateStoryboards(data.scenarios, normalBatchId);
      if (normalBatchId) closeStoryboardBatch(normalBatchId);
    }
  };
  // 切り離したランダム生成 (シェル応答) の縮退ポーリング。画面操作は一切行わず、
  // シェルが ready になるたびにシナリオブック一覧を無効化するだけ (§4.5)。
  // マウントに依存しないので latestInstanceRef の有無に関わらず onSuccess から直接呼ぶ。
  const startDetachedShellPolling = (
    track: ScenarioGenerationTrack,
    data: { jobId: string; shellScenarios: ShellScenario[] },
  ) => {
    const flow: InFlightShellFlow = {
      jobId: data.jobId,
      // 表示には使わないので placeholder は空でよい (resultMeta もダミー)。
      resultMeta: { source_type: 'custom', requested_scenarios: data.shellScenarios.length, created: true },
      placeholders: [],
      removedIds: new Set(),
      loadedIds: new Set(),
      settled: false,
      detached: true,
    };
    track.shellFlow = flow;
    // 復帰キー・in-flight registry には載せない (切り離した生成は復帰しない)。
    track.serverJobId = data.jobId;
    const settleShell = createSettleShell({
      flow,
      serverJobId: data.jobId,
      clientJobId: track.clientJobId,
      shellCount: data.shellScenarios.length,
    });
    const ctx = {
      flow,
      clientJobId: track.clientJobId,
      removeShellTab: makeRemoveShellTab(flow, track.clientJobId),
      invalidateScenarioList,
    };
    data.shellScenarios.forEach((s) => { void pollShellScenario(s, ctx).then(settleShell, settleShell); });
  };

  // 切り離した生成の完了処理。シェル応答は縮退ポーリングへ、通常応答は縮退完了処理へ振る。
  const handleDetachedGenerationOutcome = (track: ScenarioGenerationTrack, data: GenerateScenarioData) => {
    if (isShellFlowOutcome(data)) {
      // 生成中判定からの除外 (finishTrack) は全シェル確定時 (createSettleShell) に行う。
      startDetachedShellPolling(track, data);
      return;
    }
    applyDetachedGenerationResult(track, data, {
      postWriteScenarioFields: (scenarioIds, postWrite) => postWriteScenarioFields(scenarioIds, postWrite),
      invalidateScenarioList,
    });
    finishTrack(track.clientJobId);
  };

  const mutation = useMutation({
    // 復帰 effect が「進行中の同ジョブ」を二重ポーリングしないための識別キー
    // (queryClient.isMutating で参照)。
    mutationKey: SCENARIO_GENERATE_MUTATION_KEY,
    mutationFn: ({ payload, clientJobId, motionBgm }: ScenarioGenerateVariables) => {
      // clientJobId は variables で渡ってくる (ref 経由にすると「切り離し → 即再実行」で
      // 先発の mutationFn が後発の ID を読む順序依存が生まれる)。API へは payload だけ送る。
      const onJobCreated = (serverJobId: string) => {
        const track = getTrack(clientJobId);
        // 不明な track (リロード等で失われた) は attached 扱い = 従来どおり登録する。
        if (!track) return true;
        track.serverJobId = serverJobId;
        // POST 応答より先に切り離された場合は、復帰キー・in-flight registry に載せない
        // (後発の生成の復帰キーを上書きしないため。§4.9)。
        return !track.detached;
      };
      // 全編モーション (§10.5): BGM を先に決めるフロー。シェル 1 本の早期遷移で返る
      if (motionBgm) return requestMotionPrepare(payload, motionBgm, { onJobCreated });
      return requestGenerateScenario(payload, { onJobCreated });
    },
    onMutate: ({ payload, clientJobId }: ScenarioGenerateVariables) => {
      // 追加プロンプトを localStorage 履歴に積む (静止画の「追加プロンプト」と同じ挙動)。
      // 3 つの source_type 分岐すべてを 1 か所で拾うため onMutate に置く。
      const rawAdditionalPrompt = payload?.additional_prompt;
      const sentAdditionalPrompt = typeof rawAdditionalPrompt === 'string' ? rawAdditionalPrompt.trim() : '';
      if (sentAdditionalPrompt) {
        addToPromptHistory(PROMPT_HISTORY_KEYS.SCENARIO_ADDITIONAL_PROMPT, sentAdditionalPrompt);
      }
      startTrack({ clientJobId, kind: 'normal', postWrite: captureScenarioPostWrite() });
      setActiveJobs((prev) => [
        ...prev,
        { jobId: clientJobId, status: 'polling' as const, startedAt: Date.now(), type: 'scenario' as const },
      ]);
      // 結果の適用先は context でも引き継ぐ (variables と同値)。
      return { clientJobId };
    },
    onSuccess: (data, variables, context) => {
      const scenarioJobId = context?.clientJobId ?? variables.clientJobId;
      const track = scenarioJobId ? getTrack(scenarioJobId) : undefined;
      // 切り離し済み: 作業エリアへは一切反映せず、DB への後書きと一覧更新だけ行う。
      if (track?.detached) {
        handleDetachedGenerationOutcome(track, data);
        return;
      }
      // useMutation のコールバックはアンマウント後も発火する。結果の適用は最新マウントへ委譲
      // (このクロージャの useState setter は古いマウントのもので、届かない)。マウントが無ければ
      // 保留し、生成キューの項目だけ完了にする (ステータスバー・完了通知は待たせない)。
      const target = latestInstanceRef.current;
      if (target) {
        target.onGenerationSuccess(data, scenarioJobId);
      } else {
        pendingGenerationEvents.push({ kind: 'success', data, scenarioJobId, clientJobId: scenarioJobId });
        // シェル応答 (これからシェル別ポーリングが始まる) は完了ではないので、消費時の
        // settleShell に任せる。
        if (scenarioJobId && !isShellFlowOutcome(data)) {
          setActiveJobs((prev) =>
            prev.map((j) => (j.jobId === scenarioJobId ? { ...j, status: 'completed' as const } : j)),
          );
        }
      }
      // シェルフローの生成中判定はシェル別ポーリングの全確定まで続く (settleShell が finish)。
      if (scenarioJobId && !isShellFlowOutcome(data)) finishTrack(scenarioJobId);
    },
    onError: (error: unknown, variables, context) => {
      const errorMessage = resolveErrorMessage(error);
      const scenarioJobId = context?.clientJobId ?? variables.clientJobId;
      const track = getTrack(scenarioJobId);
      // 「この生成の失敗は表示しない」印。分岐に依らず必ず更新する
      // (attached の失敗なら印を消す = バナーを出す)。
      if (track?.detached) detachedErrorJobIdRef.current = scenarioJobId;
      else if (detachedErrorJobIdRef.current === scenarioJobId) detachedErrorJobIdRef.current = null;
      finishTrack(scenarioJobId);
      if (track?.detached) {
        // 切り離した生成の失敗はサイレント (キューの行も既に無い)。
        console.warn('[ScenarioGenerator] 切り離した生成が失敗しました:', scenarioJobId, errorMessage);
        return;
      }
      console.error('Scenario generation failed', error);
      // 生成キューのシナリオエントリを失敗に更新
      if (scenarioJobId) {
        setActiveJobs((prev) =>
          prev.map((j) => (j.jobId === scenarioJobId ? { ...j, status: 'failed' as const, error: errorMessage } : j)),
        );
      }
      if (isStorageQuotaError(error)) {
        toast.error(storageQuotaErrorMessage());
        return;
      }
      toast.error(`シナリオの生成に失敗しました: ${errorMessage}`);
    },
  });

  // ── Resume pending async job after page reload ──────────────
  useEffect(() => {
    let cancelled = false;
    // このマウントが開始した復帰ポーリングの行 ID (クリーンアップで手放すため。下記参照)。
    let startedQueueJobId: string | null = null;
    const pendingJobId = (() => {
      try { return sessionStorage.getItem(SCENARIO_JOB_STORAGE_KEY); } catch { return null; }
    })();

    // 同一タブ内の再マウント (生成中に別画面へ遷移して戻った等): ジョブは前のマウントが
    // 起動した非同期フロー (mutation / シェル別ポーリング) がまだ追跡中で、結果は
    // latestInstanceRef 経由でこのマウントへ渡される。ここで二重ポーリングを始めると
    // 完了時の後処理 (自動生成) が二重に走るので何もしない。activeJobs の polling 項目も
    // そのフローが完了/失敗で解決するので畳まない (ステータスバーの生成中表示を保つ)。
    // (シェルフロー進行中の再マウントでは、登録 effect が記録からスケルトンタブ等を復元済み。)
    if (pendingJobId && isScenarioJobInFlight(pendingJobId)) return;
    // アンマウント中に完了した結果は登録 effect (上) が保留から消費中。取り直さない。
    if (pendingDrainInProgress || pendingGenerationEvents.length > 0) return;
    // 既にこのタブのどこかのフロー (通常生成・シェル・前マウントの復帰) が追跡中なら何もしない。
    // 行も畳まない (そのフローが完了/失敗で解決する)。
    // これが無いと「生成中にリロード → 復帰 effect が pollScenarioJob を開始 (in-flight registry
    // には載らない) → 別画面へ → 戻る」で 2 本目の復帰 track が作られ、§4.9 の防御で 1 本目が
    // detach されて行が永久に「生成中」のまま残る。
    // 「先発を ✕ で切り離す → 即再投入 → POST 応答前に別画面へ → 戻る」でも、後発が attached の
    // まま残るのでこの return で行が保たれる。(リロード直後はレジストリが空なので従来どおり)
    if (getAttachedTrack()) return;

    // リロード前に積んだ scenario の polling 項目（client id）は、復帰系統がこちらに
    // 切り替わるため一旦畳む。実ジョブが残っていれば直後に積み直す。
    setActiveJobs((prev) => prev.filter((j) => !(j.type === 'scenario' && j.status === 'polling')));

    if (!pendingJobId || mutation.isPending) return;

    // 復帰中も他ジョブと同様にステータスバー・キューへ出し、完了/失敗で解決する。
    const queueJobId = newScenarioClientJobId();
    startedQueueJobId = queueJobId;
    setActiveJobs((prev) => [
      ...prev,
      { jobId: queueJobId, status: 'polling' as const, startedAt: Date.now(), type: 'scenario' as const },
    ]);
    // 復帰も track として登録する (生成中判定 = attached track の有無に一本化したため。§4.6)。
    // 復帰中の行も ✕ で切り離せる。後書きの値はこの時点のフォーム状態から控える
    // (リロードを跨いだため、生成開始時の値は残っていない)。
    startTrack({
      clientJobId: queueJobId,
      kind: 'resume',
      serverJobId: pendingJobId,
      postWrite: captureScenarioPostWrite(),
    });

    (async () => {
      try {
        const result = await pollScenarioJob(pendingJobId);
        const resumeTrack = getTrack(queueJobId);
        if (resumeTrack?.detached) {
          // 復帰中に切り離された: 作業エリアへは反映せず、後書きと一覧更新だけ行う。
          handleDetachedGenerationOutcome(resumeTrack, result);
          return;
        }
        finishTrack(queueJobId);
        if (!cancelled) {
          // 通常 onSuccess と同様、DB 永続化に失敗したレスポンス (created !== true) は
          // 完了扱いにせず失敗として扱う。
          if (result.created !== true) {
            mutation.reset();
            try { sessionStorage.removeItem(SCENARIO_JOB_STORAGE_KEY); } catch { /* ignore */ }
          }
          mutation.reset();
          // 保険: 結果のシナリオがすべて既に表示中 (lastResult に載っている) なら、この
          // ジョブは処理済みの再生 (replay) である (registry 解除漏れ等の防御)。通常完了として流すと全シナリオぶんの
          // 絵コンテ / ナレーション自動生成・タブ切替・完了トーストが再発火するため、
          // キュー項目を畳んで何もしない。
          const shownIds = new Set((lastResultRef.current?.scenarios ?? []).map((s) => s.id));
          const resultIds = (result.scenarios ?? []).map((s) => s.id);
          const isReplay = resultIds.length > 0 && resultIds.every((id) => shownIds.has(id));
          if (isReplay) {
            console.warn('Skipped replay of already-consumed scenario job:', pendingJobId);
            setActiveJobs((prev) => prev.filter((j) => j.jobId !== queueJobId));
            // 追跡は終わっているので復帰キーも消す (残すと次のマウントが同じ完了済み
            // ジョブをもう一度取り直し、また replay 判定で捨てることになる)。
            clearScenarioJobResumeKeyIfSettled();
            return;
          }
          handleScenarioGenerationSuccess(result, queueJobId);
        }
      } catch (err) {
        const resumeTrack = getTrack(queueJobId);
        finishTrack(queueJobId);
        if (resumeTrack?.detached) {
          // 切り離した生成の失敗はサイレント (キューの行も既に無い)。
          console.warn('[ScenarioGenerator] 切り離した復帰ジョブが失敗しました:', queueJobId, err);
          return;
        }
        if (!cancelled) {
          setActiveJobs((prev) =>
            prev.map((j) =>
              j.jobId === queueJobId ? { ...j, status: 'failed' as const, error: resolveErrorMessage(err) } : j,
            ),
          );
          console.error('Failed to resume scenario job:', err);
          try { sessionStorage.removeItem(SCENARIO_JOB_STORAGE_KEY); } catch { /* ignore */ }
        }
      }
    })();

    return () => {
      cancelled = true;
      // 復帰系統だけは結果の受け渡しが latestInstanceRef 委譲ではなく cancelled フラグ
      // (= このマウントが死んだら結果を捨て、次のマウントが張り直す) なので、attached な
      // 復帰 track を残したままにすると上の `if (getAttachedTrack()) return;` で張り直しが
      // 止まり、行が 'polling' のまま固まる。手放して次のマウントに任せる。
      // detached なら縮退処理 (後書き + 一覧更新) が続くので触らない。
      const resumeTrack = startedQueueJobId ? getTrack(startedQueueJobId) : undefined;
      if (resumeTrack && !resumeTrack.detached) finishTrack(startedQueueJobId!);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handle scenario loading from Scenario Book ──────────────
  const handleLoadScenario = async (
    scenarioId: string,
    opts?: { focus?: boolean; isAborted?: () => boolean },
  ): Promise<Scenario | null> => {
    // focus=false: 本文ロードのみ行い、アクティブタブ切替・URL 遷移・phase 変更はしない。
    // ランダム生成でバックグラウンドのシェルが ready になったとき、表示中タブを奪わないため。
    const focus = opts?.focus !== false;
    // 同シナリオに対する世代トークンを発行。後段の非同期 fetch 完了時に
    // 同 scenarioId のトークンが進んでいたら、自分の結果は捨てる。
    const myLoadToken = issueLoadToken(scenarioId);
    // 各 await の直後・状態反映の前に「もう書いてはいけない」かを判定する述語。
    //  - トークンが進んだ = 同シナリオの後続ロードが始まっている (従来の stale guard)
    //  - isAborted = 呼び出し元の中断条件 (シェルの ready ロード中に生成が切り離された等)。
    //    ロード後にタブだけ消しても、切り離し後にユーザーが変えた商品・映像スタイル・素材・
    //    URL への上書きは戻らないため、反映前に止める (計画書 §4.5)。
    const stale = () => !isLoadTokenCurrent(scenarioId, myLoadToken) || (opts?.isAborted?.() ?? false);
    // focus=false (裏で完了したシェルのロード) では全画面オーバーレイ「シナリオを読み込み中」
    // を出さない (ユーザーが別タブを編集中に割り込まないため)。
    if (focus) setLoadingScenarioCount(c => c + 1);
    try {
      const scenario = await getScenario(scenarioId);
      // 最初の await の直後。ここから下は setLastResult / setMode / URL 遷移など画面へ
      // 書き込む処理が続くので、中断していたら何も書かずに抜ける。
      if (stale()) return null;

      // Transform loaded scenario to match expected format
      const newScenario = {
        id: scenario.id,
        // 全編モーションの BGM 先行フローのシェル (本文未生成) だけは status を残し、WorkPanel が
        // BGM の確認画面 / 待ちを出せるようにする (再読み込み後の復元・§10.5)。他は従来どおり落とす
        ...(isMotionPrepareShell(scenario as { status?: string | null; generation_metadata?: Record<string, unknown> | null })
          ? { status: 'generating' as const }
          : {}),
        title: scenario.title,
        summary: scenario.summary || '',
        tags: scenario.tags || [],
        starred: scenario.starred,
        published: scenario.published,
        aspect_ratio: scenario.aspect_ratio,
        frame_mode: scenario.frame_mode,
        reference_image_urls: scenario.reference_image_urls,
        bgm_material_id: scenario.bgm_material_id,
        bgm_excluded: scenario.bgm_excluded,
        video_model: scenario.video_model,
        output_resolution: scenario.output_resolution,
        generation_metadata: scenario.generation_metadata,
        // 制作進捗と所有者。情報モーダルの進捗表示・編集可否 (所有者のみ) に使う。
        // ここで列挙しないと API が返しても lastResult に届かない (計画 §3.4.1)。
        workflow_stage_id: scenario.workflow_stage_id ?? null,
        user_id: scenario.user_id,
        // 複数AI俳優対応: 登場人物数(cap)復元のフォールバックと俳優 state 復元に必要。
        // ScenarioGenerator の復元 effect は generation_metadata 欠落時に top-level の
        // character_count 列と実俳優数(casting_models / seedance preset)を見るため、ここで落とさない。
        character_count: scenario.character_count,
        casting_model_ids: scenario.casting_model_ids,
        casting_models: scenario.casting_models,
        casting: scenario.casting,
        // 読み込み元 Scenario 型には seedance preset フィールドが無いため、state 側の形でキャスト。
        seedance_preset_actor_ids: (scenario as {
          seedance_preset_actor_ids?: string[];
          seedance_preset_actors?: {
            id: string; byteplus_asset_id: string; display_name: string;
            thumbnail_public_url?: string | null; gender?: string | null;
            min_age?: number | null; max_age?: number | null; ethnicity?: string | null;
          }[];
        }).seedance_preset_actor_ids,
        seedance_preset_actors: (scenario as {
          seedance_preset_actor_ids?: string[];
          seedance_preset_actors?: {
            id: string; byteplus_asset_id: string; display_name: string;
            thumbnail_public_url?: string | null; gender?: string | null;
            min_age?: number | null; max_age?: number | null; ethnicity?: string | null;
          }[];
        }).seedance_preset_actors,
        // 商品情報: シナリオ単位で保持。ブランドロゴ取得 (StoryboardCandidates の
        // 「ロゴを利用」等) で使う。
        asin: scenario.asin,
        product_id: scenario.source_type === 'manual'
          ? (scenario.custom_input?.product_id as string | undefined)
          : undefined,
        custom_input: scenario.custom_input,
        // 場面固有化 (P1 step4): locations は generation_metadata.locations に保存されており
        // Scenario 型にトップレベルのキーが無いため、ここで抽出する
        // (docs/features/scene-location-and-production-mode-plan.md §1.4/§2.4)。
        locations: (scenario.generation_metadata as { locations?: ScenarioLocation[] } | undefined)?.locations,
        scenes: (scenario.scenes || []).map(scene => ({
          id: scene.id,
          order: scene.order_number,
          setting: scene.setting,
          script: scene.script,
          narration: scene.narration || '',
          dialogue: scene.dialogue || '',
          // 発話単位話者帰属。[] を NULL に潰さない (docs/features/
          // scenario-dialogue-speaker-plan.md §3.1/§3.8。過去に `|| ''` 的な falsy 潰しで
          // 生成後リフレッシュのマッピングから値が欠落したバグの再発防止)。
          dialogue_lines: toUiDialogueLines(scene.dialogue_lines),
          // テロップ行ごとの話者割り当て (docs/features/scenario-telop-speaker-plan.md §3.7)。
          // [] / null を潰さない (dialogue_lines と同じ)。
          telop_lines: toUiTelopLines(scene.telop_lines),
          visual: scene.visual,
          telop: scene.telop || '',
          telop_in_frame: scene.telop_in_frame ?? 'include',
          image_url: scene.image_url,
          image_source_url: scene.image_source_url,
          image_material_id: scene.image_material_id,
          preset_image_url: scene.preset_image_url,
          preset_image_material_id: scene.preset_image_material_id,
          include_product: scene.include_product ?? true,
          // シーン単位のフレーム生成調整 (scene-frame-generation-tuning-plan.md §5)。落とすと
          // 再読込後に調整ダイアログが空になり、再生成に追加プロンプトが乗らない
          frame_additional_prompt: scene.frame_additional_prompt ?? null,
          frame_reference_overrides: scene.frame_reference_overrides ?? null,
          // 場面固有化 (P1 step4)。落とすとフレーム生成 payload に載らず、場面一貫性指示
          // (build_scene_intro_prompt) が発火しない。
          location_key: scene.location_key,
          continues_previous_shot: scene.continues_previous_shot ?? false,
          // 顔フラグ。欠損 (旧シナリオ・カラム未適用) は undefined のまま通し、
          // 判定側 (isFaceSafeSeedanceScene) で顔あり扱いにフォールバックする。
          has_visible_face: scene.has_visible_face,
          last_frame_url: scene.last_frame_url,
          last_frame_material_id: scene.last_frame_material_id,
          video_last_frame_url: scene.video_last_frame_url,
          video_last_frame_source_url: scene.video_last_frame_source_url,
          video_last_frame_material_id: scene.video_last_frame_material_id,
          video_material_id: scene.video_material_id,
          video_duration_seconds: scene.video_duration_seconds,
          storyboard_duration_seconds: scene.storyboard_duration_seconds,
          video_has_audio: scene.video_has_audio,
          // 「採用しない」トグル。落とすとリロード後に除外状態が見かけ上リセットされる。
          video_excluded: scene.video_excluded,
          // 「前の動画とつなげる」トグル。落とすと下の loadedSceneVideoSettings の
          // 復元 (linkPreviousVideo) が常に false になり、リロードで OFF に戻ってしまう。
          link_previous_video: scene.link_previous_video,
          // シーンの動画入力方式の明示値 (P2 step 2)。落とすと deriveSceneExtendMode
          // 等の優先順位1 (明示値) が常に未設定扱いになる。書き込み UI は次 step。
          video_input_mode: scene.video_input_mode,
          // 同トグルの参照元シーン明示指定 (§10 B案)。同上の復元経路で使う。
          link_previous_source_scene_id: scene.link_previous_source_scene_id,
          // 同トグルの静止画フレーム除外 (§11)。同上。
          link_previous_frame_excluded: scene.link_previous_frame_excluded,
          narration_excluded: scene.narration_excluded,
          narration_material_id: scene.narration_material_id,
          narration_voice_id: scene.narration_voice_id,
          narration_duration_seconds: scene.narration_duration_seconds,
          // 前面音声の種別とサウンドロゴ素材。これらを落とすとリロード後に
          // audio_track_type が undefined となりナレーション扱いに戻ってしまう。
          audio_track_type: scene.audio_track_type,
          sound_logo_material_id: scene.sound_logo_material_id,
          sound_logo_duration_seconds: scene.sound_logo_duration_seconds,
          generation_metadata: scene.generation_metadata,
          // ブランドカット(タイトル/エンドカード)情報。これらを落とすとリロード後に
          // ブランドカットとして認識されず、想定尺やサムネイル表示・本編動画の判定が崩れる。
          brand_cut_position: scene.brand_cut_position,
          brand_cut_duration_seconds: scene.brand_cut_duration_seconds,
          image_prompt: scene.image_prompt,
          video_prompt: scene.video_prompt,
        }))
      };

      // 読み込んだシナリオの商品情報
      const loadedProductInfo: Partial<ScenarioResponse> = {
        source_type: scenario.source_type,
        asin: scenario.asin,
        custom_input: scenario.custom_input,
        product_id: scenario.source_type === 'manual'
          ? (scenario.custom_input?.product_id as string | undefined)
          : undefined,
      };

      // 既存のシナリオがあれば末尾に追加、なければ新規作成
      // 関数型更新で最新の state を参照（連続呼び出し時のクロージャ問題を回避）
      setLastResult(prev => {
        if (prev && prev.scenarios.length > 0) {
          const existingIndex = prev.scenarios.findIndex(s => s.id === scenario.id);
          if (existingIndex >= 0) {
            const updatedScenarios = [...prev.scenarios];
            updatedScenarios[existingIndex] = newScenario;
            return {
              ...prev,
              ...loadedProductInfo,
              scenarios: updatedScenarios
            };
          }
          return {
            ...prev,
            ...loadedProductInfo,
            scenarios: [...prev.scenarios, newScenario]
          };
        }
        return {
          ...loadedProductInfo as Pick<ScenarioResponse, 'source_type'>,
          asin: scenario.asin,
          custom_input: scenario.custom_input,
          requested_scenarios: 1,
          scenarios: [newScenario]
        };
      });

      // ── 動画生成モード判定 + 動画尺の既定値 ───────────────────────────────
      // これらは「後段の素材復元 (await を伴い、stale guard で早期 return しうる)」より
      // 前で確定させる。await の後ろに置くと、ランダム生成の並行ポーリング等で同一
      // シナリオの再ロードが重なってトークンが進んだ際、setSceneVideoSettings に到達せず
      // 動画ボックスが getSceneVideoSettings の fallback (4秒) のまま固定されてしまう
      // (リロードは race が無いため正しい尺になる、という非対称バグの原因)。

      // 動画生成モードの判定。正本は generation_metadata.production_mode /
      // per_scene_default_mode で、無い/不正な旧データのみ generation_metadata.video_generation_mode
      // → frame_mode (ボード未生成で storyboard へ未昇格のケースがあるためのフォールバック) の順に
      // 導出する (docs/features/scene-location-and-production-mode-plan.md §5)。
      // 注: scenes[].generation_metadata.reference_image_urls はフレームモードの動画生成
      //   (俳優モデル/last_frame) でも保存されるため、それを判定材料にすると誤検知する。
      const derivedVideoMode = deriveLoadedVideoGenerationMode(newScenario);
      const isReferenceMode = derivedVideoMode === 'reference';
      const isStoryboardMode = derivedVideoMode === 'storyboard';
      const isFrameMode = derivedVideoMode === 'frame';

      // 音声生成は常時オン (トグルは 2026-09-14 に撤廃)。
      // 保存済み video_duration_seconds があれば最優先。無い場合の既定値は:
      //   - ストーリーボード: コマ尺合計 (想定尺は使わない)
      //   - それ以外の連続尺モデル (Seedance / MiniMax H3): 想定尺ベース (frame は /シーン数、それ以外はそのまま)
      //   - 参照画像モードで生成されたシーン: 8 秒
      //   - その他: 4 秒
      const loadedVideoModel = resolveUsableVideoModel(newScenario.video_model as string | undefined, videoSettings.videoModel);
      const loadedResolution = newScenario.output_resolution || videoSettings.resolution || '1080';
      const loadedScenarioMeta = newScenario.generation_metadata as Record<string, unknown> | undefined;
      // 映像スタイルを復元 (フレーム/動画生成 payload に正しいスタイルを送るため)。
      // raw key を保持する (OEM 独自 key を潰さない)。文字列でなければ photorealistic。
      const loadedShooting = splitShootingMethod(loadedScenarioMeta);
      setVisualStyle(loadedShooting.visualStyle);
      setShootingMethod(loadedShooting.shootingMethod);
      setDialect(normalizeDialect(loadedScenarioMeta));
      // ターゲット性別・年代 (generation_metadata.target_gender / target_age)。旧シナリオは未保存 → 指定なし
      setTargetGender(normalizeTargetKey(loadedScenarioMeta?.target_gender));
      setTargetAge(normalizeTargetKey(loadedScenarioMeta?.target_age));
      // 「モーショングラフィックス」の3択 (§10.14)。旧シナリオ (visual_style='motion_graphics') は settingsContext の
      // effect が whole へ読み替える (ツリーに field があるとき)
      setMotionGraphics(normalizeMotionGraphics(loadedScenarioMeta?.motion_graphics));
      const loadedIntendedTotalSeconds = parseIntendedDurationSeconds(
        typeof loadedScenarioMeta?.duration === 'string' ? (loadedScenarioMeta.duration as string) : null,
      );
      const evenSplitDefaultLoad = computeEvenSplitDefaultDuration(
        loadedVideoModel,
        loadedIntendedTotalSeconds,
        newScenario.scenes.length,
        supportedDurationsOf(loadedVideoModel),
      );
      // ストーリーボードモードは保存値が無ければコマ尺合計を既定にする (想定尺は使わない)。
      // モデル上限へのクランプ・離散候補へのスナップは SceneItem 側で行う。
      const loadedStoryboardTotalDuration = computeStoryboardTotalDuration(newScenario.scenes);
      const loadedSceneVideoSettings: Record<string, { duration: number; generateAudio: boolean; videoModel?: string; resolution?: string; linkPreviousVideo?: boolean; linkPreviousSourceSceneId?: string | null; linkPreviousFrameExcluded?: boolean }> = {};
      newScenario.scenes.forEach(scene => {
        const key = `${newScenario.id}-scene-${scene.order}`;
        // 生成直後経路 (handleScenarioGenerationSuccess) と同じ純関数で既定尺を決める。
        // 旧実装の「参照画像ありなら 8 秒」フォールバックは Veo (参照画像 8 秒固定) の名残で、
        // Veo 廃止 (639cadf57) 後は根拠が無いため 4 秒に統一した。
        const duration = resolvePerSceneDefaultDuration({
          isStoryboardMode,
          storedDuration: (scene as { video_duration_seconds?: number | null }).video_duration_seconds,
          hasGeneratedVideo: !!(scene as { video_material_id?: string | null }).video_material_id,
          storyboardTotalDuration: loadedStoryboardTotalDuration,
          evenSplitDefault: evenSplitDefaultLoad,
        });
        loadedSceneVideoSettings[key] = {
          duration,
          generateAudio: true,
          videoModel: loadedVideoModel,
          resolution: loadedResolution,
          // 「前の動画とつなげる」の保存値を復元 (scenes.link_previous_video)。
          // true でも前の動画が未生成なら SceneVideoBox 側で非活性のまま。
          // DB 未設定 (null) は undefined のまま保つ: getSceneVideoSettings の
          // 未設定時デフォルト導出 (参照画像モード × 場面継続 × 対応モデル → ON) の
          // 対象にするため、!! で false に潰さない (明示 OFF の false とは区別する)。
          linkPreviousVideo: (scene as { link_previous_video?: boolean | null }).link_previous_video ?? undefined,
          // 参照元シーンの明示指定 (§10 B案)。無効な指定 (シーン削除等) は生成時に
          // BE が直前へフォールバックする。
          linkPreviousSourceSceneId: (scene as { link_previous_source_scene_id?: string | null }).link_previous_source_scene_id ?? null,
          // 静止画フレーム除外 (§11)。
          linkPreviousFrameExcluded: !!(scene as { link_previous_frame_excluded?: boolean }).link_previous_frame_excluded,
        };
      });
      setSceneVideoSettings(prev => ({ ...prev, ...loadedSceneVideoSettings }));

      // 追加したシナリオを選択状態にする (focus=false のバックグラウンドロードでは奪わない)
      if (focus) {
        setActiveScenarioId(scenario.id);
      }

      // モードとASINを設定。focus=false (裏で完了したシェル) では現在の編集フォーム
      // (mode / 商品 / ターゲット等のグローバル state) を上書きしない。
      if (focus && scenario.source_type === 'asin') {
        setMode('asin');
        if (scenario.asin) {
          setAsin(scenario.asin);
        }
        setManualProductId(null);
        setManualProductTitle(null);
      } else if (focus && scenario.source_type === 'custom') {
        setMode('custom');
        const customInput = scenario.custom_input;
        if (customInput) {
          _setProductName((customInput.product_name ?? '') as string);
          _setBrandName((customInput.brand_name ?? '') as string);
          const sellingPointsValue = customInput.selling_points;
          if (Array.isArray(sellingPointsValue)) {
            setSellingPoints(sellingPointsValue.join('\n'));
          } else {
            setSellingPoints((sellingPointsValue ?? '') as string);
          }
          setGoal((customInput.goal ?? '') as '' | 'price' | 'branding' | 'awareness');
          setTargetAudience((customInput.target_audience ?? '') as string);
          _setTone((customInput.tone ?? '') as string);
          _setPricePoint((customInput.price_point ?? '') as string);
        }
      } else if (focus && scenario.source_type === 'manual') {
        setMode('manual');
        setScenarioInputTab('product');
        setAsin('');
        const customInput = scenario.custom_input;
        if (customInput) {
          setManualProductId((customInput.product_id ?? null) as string | null);
          setManualProductTitle((customInput.product_name ?? null) as string | null);
        }
      }

      // 商品画像を新スコープに追加（キャッシュから同期的に取得）
      const tempItems = tempMaterials.data?.pages.flatMap(page => page.items) || [];
      const scenarioAsin = scenario.asin;
      const manualProdId = scenario.source_type === 'manual'
        ? (scenario.custom_input?.product_id as string | undefined)
        : undefined;
      const PRODUCT_SOURCE_TYPES = ['asin', 'url', 'upload'];

      // 保存済み user_selected_asset_ids がある場合は exact restore に切り替える。
      // 配列 (空配列含む) であれば「ユーザーが明示的に保存した選択」とみなし、
      // 商品画像の自動補完を skip して saved selection 通りに scope を組み立てる。
      // null / undefined の場合は legacy 扱いで従来の自動補完を実行する。
      const savedAssetIdsRaw = (scenario.generation_metadata as Record<string, unknown> | null | undefined)
        ?.user_selected_asset_ids;
      const savedAssetIds = Array.isArray(savedAssetIdsRaw)
        ? (savedAssetIdsRaw as unknown[]).filter((x): x is string => typeof x === 'string' && Boolean(x))
        : null;

      // 空配列 [] は本来「ユーザーが明示的に何も選ばなかった」を表すが、生成時に
      // 商品素材が scope に乗る前に生成されると [] が保存され、商品が永久に復元され
      // なくなる事象がある。商品 (ASIN / 手動) が紐付くシナリオで [] のときは取りこぼし
      // とみなし、legacy 自動補完へフォールバックして商品画像を復元する。
      // 商品が無い custom シナリオの [] は尊重する (exact restore = 空のまま)。
      const hasProduct = Boolean(scenarioAsin || manualProdId);
      const useExactRestore = savedAssetIds !== null && !(savedAssetIds.length === 0 && hasProduct);

      // material → GenerationAsset 変換は exact restore と legacy 自動補完の両方で使う。
      const materialToAsset = (material: any): GenerationAsset => {
        const urls = resolveMaterialUrls(material);
        return {
          id: material.id,
          thumbnailUrl: urls.thumbnail || '',
          sourceUrl: resolveAiSourceUrl(material, urls),
          type: 'temporary',
          name: material.name ?? null,
          storageBucket: material.storage_bucket || null,
          storagePath: material.storage_path || null,
          contentType: material.content_type || null,
          // 生成フレーム (source_type='scenario') の識別に使う (isGeneratedSceneFrameAsset)。
          // user_selected_asset_ids には自動追加された生成フレームも保存されているため、
          // 復元経路でも sourceType を乗せないと動画生成の参照画像へ流れてしまう。
          sourceType: material.source_type || null,
          usage: 'reference',
          asin: material.asin || scenarioAsin,
          hasProductLinks: true,
          width: material.width || null,
          height: material.height || null,
          aspectRatio: material.aspect_ratio || calculateAspectRatio(material.width, material.height),
        };
      };

      // 防御: scope が既に populate されている (= mutation.onSuccess の migrate 等で既に
      // 正しい状態) なら、auto-complete も exact restore も実行しない。
      // recentlyCreatedScenarioIdsRef での race 防御を補強する 2 段目のガード。
      const alreadyPopulated = activeScenarioId === scenario.id && selectedAssets.length > 0;

      if (alreadyPopulated) {
        // scope は既に正しい状態。何もしない。
      } else if (useExactRestore) {
        // ── exact restore: 保存済み selection を scope へ丸ごと反映 ──
        // useExactRestore=true の時点で savedAssetIds は non-null (TS narrow 補助)
        const exactIds = savedAssetIds ?? [];
        // 1. キャッシュ (個人フォルダ・共有フォルダ) を集めて id → material のマップを作る
        const cacheMap = new Map<string, any>();
        for (const m of tempItems) {
          if (m?.id) cacheMap.set(m.id, m);
        }
        // 2. キャッシュに無い id を fetchMaterialById で並列取得
        const missingIds = exactIds.filter(id => !cacheMap.has(id));
        const fetched = await Promise.all(
          missingIds.map(id => fetchMaterialById(id).catch(() => null)),
        );
        // stale guard: 同シナリオの再ロードが進んでいたら、この結果は捨てる
        if (stale()) return null;
        for (let i = 0; i < missingIds.length; i++) {
          const mat = fetched[i];
          if (mat) cacheMap.set(missingIds[i], mat);
        }
        // 3. 保存順を維持して GenerationAsset 配列を組み立てる (削除済みは silent skip)
        const restored: GenerationAsset[] = [];
        for (const id of exactIds) {
          const mat = cacheMap.get(id);
          if (!mat) continue;
          restored.push(materialToAsset(mat));
        }
        // 4. silent: true でスコープを丸ごと差し替える (保存トリガを発火させない)
        replaceAssetsInScope(scenario.id, restored, { silent: true });
        // 自動補完 (productMaterials.forEach / ASIN fetch / manual product fetch) はすべて skip
      } else {
        // ── legacy: 保存済み selection が無いシナリオは従来通り商品画像を自動補完 ──
        const productMaterials = tempItems.filter(m => {
          if (!m.content_type?.startsWith('image/')) return false;
          if (m.source_type && !PRODUCT_SOURCE_TYPES.includes(m.source_type)) return false;
          if (scenarioAsin) {
            const matAsins = Array.isArray(m.asin) ? m.asin : (m.asin ? [m.asin] : []);
            if (matAsins.includes(scenarioAsin)) return true;
          }
          if (manualProdId && m.product_links?.some((pl: { product_id: string }) => pl.product_id === manualProdId)) return true;
          return false;
        });

        // legacy 自動補完経路はユーザー操作ではないので silent: true で markDirty 抑止。
        const addMaterialToScenarioScope = (material: any) => {
          addAssetToScope(scenario.id, materialToAsset(material), { silent: true });
        };

        if (productMaterials.length > 0) {
          productMaterials.forEach(addMaterialToScenarioScope);
        } else {
          if (scenarioAsin) {
            setFetchedAsins((prev) => new Set(prev).add(scenarioAsin));
            fetchAsinMaterials(scenarioAsin).then(materials => {
              if (stale()) return;
              if (materials && materials.length > 0) {
                materials.forEach(addMaterialToScenarioScope);
                queryClient.refetchQueries({ queryKey: ['temporary-materials'] });
              }
            }).catch(err => {
              console.error('Failed to fetch ASIN materials for loaded scenario:', err);
              setFetchedAsins((prev) => {
                const next = new Set(prev);
                next.delete(scenarioAsin!);
                return next;
              });
            });
          }
          if (manualProdId) {
            setFetchedProductIds((prev) => new Set(prev).add(manualProdId));
            getProduct(manualProdId).then(async (product) => {
              if (stale()) return;
              const imageUrls: string[] = [];
              if (product.main_image_url) imageUrls.push(product.main_image_url);
              if (product.additional_images && Array.isArray(product.additional_images)) {
                for (const img of product.additional_images) {
                  if (typeof img === 'string') imageUrls.push(img);
                  else if (img && typeof img === 'object' && 'url' in img && typeof img.url === 'string') imageUrls.push(img.url);
                }
              }
              for (const url of imageUrls) {
                try {
                  const material = await saveImageToTemporary(url, undefined, {
                    product_id: manualProdId!,
                    product_type: 'manual',
                  });
                  if (stale()) return;
                  addMaterialToScenarioScope(material);
                } catch (err) {
                  console.error('Failed to save image for loaded scenario:', url, err);
                }
              }
              queryClient.refetchQueries({ queryKey: ['temporary-materials'] });
          }).catch(err => {
            console.error('Failed to fetch manual product materials for loaded scenario:', err);
            setFetchedProductIds((prev) => {
              const next = new Set(prev);
              next.delete(manualProdId!);
              return next;
            });
          });
        }
      }
      } // savedAssetIds 分岐の outer else を閉じる

      // フレームモード: 旧仕様 (useFirstFrameSync の確定素材自動同期。廃止済み) で
      // silent 追加された確定フレーム素材が選択素材に残っている場合、除去する
      // (docs/features/link-previous-scene-continuity-default-plan.md §9 step-1 項目8)。
      // 安全に消せる条件 = 保存済み user_selected_asset_ids に含まれていないこと
      // (自動挿入は silent: true で保存トリガを発火させないため、由来を保存値の
      // 有無で判別できる。明示選択された同一素材は保存値に入っているので残す)。
      if (isFrameMode) {
        newScenario.scenes.forEach((scene) => {
          const confirmedId = scene.image_material_id;
          if (!confirmedId) return;
          if (savedAssetIds && savedAssetIds.includes(confirmedId)) return;
          if (selectedAssets.some((a) => a.id === confirmedId)) {
            removeAssetFromScope(newScenario.id, confirmedId, { silent: true });
          }
        });
      }

      // キャスティング・Seedance プリセット・動画設定は「現在の編集対象」のグローバル
      // state。focus=false (裏で完了したシェル) では表示中タブの設定を壊さないため復元しない。
      if (focus) {
        // キャスティング（AI俳優）を復元。
        // 複数AI俳優では casting_models[] を正とし、単数 casting は旧データ用 fallback。
        // casting が欠けたレスポンスでも casting_models[0] を落とさない。
        const castingList = (scenario.casting_models && scenario.casting_models.length > 0)
          ? scenario.casting_models
          : (scenario.casting ? [scenario.casting] : []);
        const toCastingItem = (c: typeof castingList[number]) => ({
          id: c.id,
          display_name: c.display_name,
          face_image_public_url: c.face_image_public_url,
          character_sheet_public_url: c.character_sheet_public_url,
          gender: c.gender,
          min_age: c.min_age,
          max_age: c.max_age,
          ethnicity: c.ethnicity,
        });
        setSelectedCasting(castingList.length > 0 ? toCastingItem(castingList[0]) : null);
        setAdditionalCastings(castingList.slice(1).map(toCastingItem));

        // Seedance プリセットアクター復元 (casting と排他)。複数対応: seedance_preset_actors[]
        // 全件 (順序保持) を主(=[0]) + 追加(=[1..]) に振り分ける。後方互換: 配列が無ければ
        // 単数 seedance_preset_actor を主のみに。
        type SdActor = {
          id: string;
          byteplus_asset_id: string;
          display_name: string;
          thumbnail_public_url?: string | null;
          gender?: string | null;
          min_age?: number | null;
          max_age?: number | null;
          ethnicity?: string | null;
        };
        const sdScenario = scenario as unknown as {
          seedance_preset_actor?: SdActor;
          seedance_preset_actors?: SdActor[];
        };
        const sdList: SdActor[] = (sdScenario.seedance_preset_actors && sdScenario.seedance_preset_actors.length > 0)
          ? sdScenario.seedance_preset_actors
          : (sdScenario.seedance_preset_actor ? [sdScenario.seedance_preset_actor] : []);
        const toSdItem = (a: SdActor) => ({
          id: a.id,
          byteplus_asset_id: a.byteplus_asset_id,
          display_name: a.display_name,
          thumbnail_public_url: a.thumbnail_public_url ?? undefined,
          gender: a.gender ?? undefined,
          min_age: a.min_age ?? undefined,
          max_age: a.max_age ?? undefined,
          ethnicity: a.ethnicity ?? undefined,
        });
        setSelectedSeedancePresetActor(sdList.length > 0 ? toSdItem(sdList[0]) : null);
        setAdditionalSeedancePresetActors(sdList.slice(1).map(toSdItem));

        // アスペクト比・動画モデル・解像度を復元
        const restoredAspectRatio = (scenario.aspect_ratio === '16:9' || scenario.aspect_ratio === '9:16')
          ? scenario.aspect_ratio
          : '16:9';
        setVideoSettings(prev => ({
          ...prev,
          aspectRatio: restoredAspectRatio,
          ...(scenario.video_model && { videoModel: scenario.video_model as typeof prev.videoModel }),
          ...(scenario.output_resolution && { resolution: scenario.output_resolution as typeof prev.resolution }),
        }));
      }

      // 各シーンの画像をscenePreviewsとscenePreviewMaterialsに追加
      const newScenePreviews: Record<string, string[]> = {};
      const newScenePreviewMaterials: Record<string, any[]> = {};
      const newConfirmedFirstFrames: Record<string, string> = {};

      newScenario.scenes.forEach(scene => {
        const key = `${newScenario.id}-scene-${scene.order}`;
        let displayUrl = scene.image_url;
        let sourceUrl = (scene as any).image_source_url || scene.image_url;
        let materialId = scene.image_material_id;

        if (!displayUrl && scene.continues_previous_shot && scene.order > 1) {
          const prevScene = newScenario.scenes.find(s => s.order === scene.order - 1);
          // 前シーンに生成済み動画があればその終わり絵 (video_last_frame_*) を優先し、
          // 無ければユーザーの「最後のフレーム」枠 (last_frame_*) を使う
          if (prevScene?.video_last_frame_url) {
            displayUrl = prevScene.video_last_frame_url;
            sourceUrl = prevScene.video_last_frame_source_url || prevScene.video_last_frame_url;
            materialId = prevScene.video_last_frame_material_id || undefined;
          } else if (prevScene?.last_frame_url) {
            displayUrl = prevScene.last_frame_url;
            sourceUrl = (prevScene as any).last_frame_source_url || prevScene.last_frame_url;
            materialId = prevScene.last_frame_material_id || undefined;
          }
        }

        if (displayUrl) {
          newScenePreviews[key] = [displayUrl];
          const resolvedMaterialId = materialId || `scene-${scene.id}-image`;
          const material = {
            id: resolvedMaterialId,
            preview_url: displayUrl,
            source_url: sourceUrl,
            thumbnail_url: displayUrl,
            content_type: 'image/jpeg',
            storage_bucket: 'temporary',
            storage_path: null,
          };
          newScenePreviewMaterials[key] = [material];
          if (materialId) {
            newConfirmedFirstFrames[key] = resolvedMaterialId;
          }
        }
      });

      setScenePreviews(prev => ({ ...prev, ...newScenePreviews }));
      setScenePreviewMaterials(prev => ({ ...prev, ...newScenePreviewMaterials }));
      setConfirmedFirstFrames(prev => ({ ...prev, ...newConfirmedFirstFrames }));

      // 最後のフレームを復元 (商品含めるトグルは廃止。scene-frame-generation-tuning-plan.md §2.3)
      const loadedLastFrames: Record<string, { url: string; sourceUrl?: string; materialId: string } | null> = {};
      newScenario.scenes.forEach(scene => {
        const key = `${newScenario.id}-scene-${scene.order}`;
        if (scene.last_frame_url) {
          loadedLastFrames[key] = {
            url: scene.last_frame_url,
            sourceUrl: (scene as any).last_frame_source_url || scene.last_frame_url,
            materialId: scene.last_frame_material_id || '',
          };
        }
      });
      setSceneLastFrames(prev => ({ ...prev, ...loadedLastFrames }));

      // derivedVideoMode / isReferenceMode / isStoryboardMode と動画尺の既定初期化は
      // setLastResult 直後へ移動済み (素材復元の await・stale guard 早期 return より前に
      // 確定させるため)。以降はそこで宣言された値を参照する。

      // 最後のフレームのパネルは自動では開かない (ボタンの色で設定済みが分かる。scene-frame-generation-tuning-plan.md §4.4)

      // 各シーンのカルーセルインデックスを0に初期化
      const newScenePreviewIndex: Record<string, number> = {};
      newScenario.scenes.forEach(scene => {
        const key = `${newScenario.id}-scene-${scene.order}`;
        newScenePreviewIndex[key] = 0;
      });
      setScenePreviewIndex(prev => ({ ...prev, ...newScenePreviewIndex }));

      // 動画尺の既定値・映像スタイル復元は setLastResult 直後へ移動済み (上記参照)。

      // 読み込んだシナリオの frame_mode に応じて videoGenerationMode を必ず設定
      // (else を省略すると localStorage に残っていた前回モードを引き継いでしまうため)
      // videoGenerationMode は「現在の編集対象」のグローバル mode。focus=false では
      // 表示中タブのモードを奪わない (各シナリオ別の scene state / asset scope は下で復元)。
      if (isStoryboardMode) {
        // ストーリーボードモード: 絵コンテ候補は StoryboardCandidates が
        // useStoryboards で別途取得するため、ここでは asset 復元は不要。
        if (focus) setVideoGenerationModeTransient('storyboard');
      } else if (isReferenceMode) {
        if (focus) setVideoGenerationModeTransient('reference');
        // exact restore 済み (replaceAssetsInScope) の場合はそれが真実の source。
        // reference_image_urls / scene.last_frame_material_id からの追加はユーザーが外した
        // 素材を再注入してしまうので skip する。逆に legacy / 取りこぼし [] 復元 (=
        // useExactRestore=false) のときは従来通り参照画像・最終フレームを復元する。
        if (!useExactRestore) {
          // 参照画像アセットを復元 (DB の reference_image_urls からの復元 = silent: true で
          // user_selected_asset_ids の保存トリガを発火させない)
          const savedRefImages = newScenario.reference_image_urls as Array<{ id: string; sourceUrl: string; thumbnailUrl: string }> | null;
          if (savedRefImages && Array.isArray(savedRefImages) && savedRefImages.length > 0) {
            savedRefImages.forEach(img => {
              addAssetToScope(newScenario.id, {
                id: img.id,
                sourceUrl: img.sourceUrl,
                thumbnailUrl: img.thumbnailUrl,
                type: 'temporary',
                usage: 'reference',
              }, { silent: true });
            });
          }
          // シーンの最後のフレームもselectedAssetsに追加
          newScenario.scenes.forEach(scene => {
            if (scene.last_frame_url && scene.last_frame_material_id) {
              const lastFrameUrl = (scene as any).last_frame_source_url || scene.last_frame_url;
              // 重複チェック（savedRefImagesに含まれていなければ追加）
              const alreadyAdded = savedRefImages?.some(img => img.id === scene.last_frame_material_id);
              if (!alreadyAdded) {
                addAssetToScope(newScenario.id, {
                  id: scene.last_frame_material_id,
                  sourceUrl: lastFrameUrl,
                  thumbnailUrl: scene.last_frame_url,
                  type: 'temporary',
                  usage: 'reference',
                }, { silent: true });
              }
            }
          });
        }
      } else {
        if (focus) setVideoGenerationModeTransient('frame');
      }

      // 動画素材が生成済みのシーンの状態を復元
      const loadedVideoMaterialIds: Record<string, string> = {};
      const loadedVideoGenerated: Record<string, boolean> = {};
      newScenario.scenes.forEach(scene => {
        const key = `${newScenario.id}-scene-${scene.order}`;
        if (scene.video_material_id) {
          loadedVideoMaterialIds[key] = scene.video_material_id;
          loadedVideoGenerated[key] = true;
        }
      });
      setSceneVideoMaterialId(prev => ({ ...prev, ...loadedVideoMaterialIds }));
      setSceneVideoGenerated(prev => ({ ...prev, ...loadedVideoGenerated }));

      // 参照画像モード: image_urlがないが動画生成済みのシーンは、動画マテリアルのサムネイルをプレビューに設定
      if (isReferenceMode) {
        newScenario.scenes.forEach(scene => {
          const key = `${newScenario.id}-scene-${scene.order}`;
          if (!newScenePreviews[key]?.length && scene.video_material_id) {
            fetchMaterialById(scene.video_material_id).then(mat => {
              if (!mat) return;
              const thumbUrl = (mat as any).preview_url || (mat as any).thumbnail_url;
              if (!thumbUrl) return;
              setScenePreviews(prev => {
                if (prev[key]?.length) return prev;
                return { ...prev, [key]: [thumbUrl] };
              });
              setScenePreviewMaterials(prev => {
                if (prev[key]?.length) return prev;
                return { ...prev, [key]: [{ id: scene.video_material_id!, source_url: thumbUrl, preview_url: thumbUrl, thumbnail_url: thumbUrl }] };
              });
            }).catch(() => {});
          }
        });
      }

      // 「音声AIモデル」(プロバイダ) の復元は NarrationSection が voice-map のナレーターの声 (provider 付き) から行う。
      // 以前は生成済みの声 id を 3 プロバイダの一覧から逆引きしていたが、一覧の取得 (Gemini は 20 秒近い) を待って
      // 後から上書きするため、利用者が切り替えた直後に元へ戻る事故が起きていた

      // URL更新・作業画面遷移は focus 時のみ (バックグラウンドのシェル ready ロードでは奪わない)
      if (focus) {
        navigate(`/studio/video/${scenarioId}`, { replace: false });
        changePhase('work');
      }

      // ロード成功。シェルポーリングはこの戻り値 (非 null) を見て自動生成後処理を発火する。
      return newScenario as Scenario;
    } catch (error) {
      console.error('Failed to load scenario:', error);
      // 裏で完了したシェルのロード失敗は、表示中タブの操作を奪わない (静かに失敗)。
      // 呼び出し側 (シェルポーリング) は null を見てリトライする。
      if (focus) {
        toast.error('シナリオの読み込みに失敗しました');
        localStorage.removeItem('studio_active_scenario_id');
        navigate('/studio/video', { replace: true });
        // URL 直リンクで失敗したケース: 初期 phase が 'work' のままだと
        // 空のワークパネルが残るのでセットアップに戻す
        changePhase('setup');
      }
      return null;
    } finally {
      if (focus) setLoadingScenarioCount(c => c - 1);
    }
  };

  /** 複数シナリオを一括読み込み（チェック順に追加、URLは1つ目） */
  const handleLoadScenarios = async (scenarioIds: string[]) => {
    if (scenarioIds.length === 0) return;
    // 内部の handleLoadScenario の finally でカウンタが 0 に戻る瞬間を作らないよう
    // 外側でも 1 加算しておく
    setLoadingScenarioCount(c => c + 1);
    try {
      for (let i = 0; i < scenarioIds.length; i++) {
        await handleLoadScenario(scenarioIds[i]);
      }
      // URLとアクティブシナリオを1つ目に設定
      const firstId = scenarioIds[0];
      setActiveScenarioId(firstId);
      navigate(`/studio/video/${firstId}`, { replace: true });
      toast.success(`${scenarioIds.length}件のシナリオを読み込みました`);
    } catch (error) {
      console.error('Failed to load scenarios:', error);
      toast.error('一部のシナリオの読み込みに失敗しました');
    } finally {
      setLoadingScenarioCount(c => c - 1);
    }
  };

  // ── 開いているシナリオタブを sessionStorage に保存する ──
  // ID の並び (= タブの順序) とアクティブ ID が変わったときだけ書く。全タブを閉じた /
  // クリアしたときは削除する (最初から空の状態 = ページ入場直後は何もしない。復元前に消さないため)。
  const openTabIdsKey = (lastResult?.scenarios ?? []).map(s => s.id).join(',');
  const hadOpenTabsRef = useRef(false);
  useEffect(() => {
    if (openTabIdsKey) {
      hadOpenTabsRef.current = true;
      writeStoredOpenTabs({ ids: openTabIdsKey.split(','), activeId: activeScenarioId ?? null });
    } else if (hadOpenTabsRef.current) {
      hadOpenTabsRef.current = false;
      writeStoredOpenTabs(null);
    }
  }, [openTabIdsKey, activeScenarioId]);

  // 保存済みタブの復元は 1 回だけ (React Strict Mode の二重実行や searchParams 変化で
  // 二重に読み込まないよう、状態を ref で持つ)。
  const restoreOpenTabsRef = useRef<'idle' | 'running' | 'done'>('idle');

  // 進行中のシェルフローの表示を、このマウントへ復元する (復帰 effect から呼ぶ)。
  // シェル別ポーリング自体は投入元のクロージャが継続し、ready 後の本文ロードは
  // latestInstanceRef 経由でこのマウントに届く。
  const restoreInFlightShellFlow = (flow: InFlightShellFlow) => {
    const remaining = flow.placeholders.filter((p) => !flow.removedIds.has(p.id));
    if (remaining.length === 0) return;
    // URL effect / 保存済みタブ復元が同じシェルを (status 抜きで) 二重ロードしないよう、
    // 生成直後と同じ扱いにする。
    restoreOpenTabsRef.current = 'done';
    remaining.forEach((p) => recentlyCreatedScenarioIdsRef.current.add(p.id));
    window.setTimeout(() => {
      remaining.forEach((p) => recentlyCreatedScenarioIdsRef.current.delete(p.id));
    }, 5000);
    setLastResult({ ...flow.resultMeta, scenarios: remaining });
    const current = activeScenarioIdRef.current;
    const active = current && remaining.some((p) => p.id === current) ? current : remaining[0].id;
    setActiveScenarioId(active);
    // 直後の保留イベント消費 (handleReadyShell の focus 判定) が state 反映前でも新しい
    // アクティブタブを見るよう、ミラー ref を同期的に更新しておく。
    activeScenarioIdRef.current = active;
    navigate(`/studio/video/${active}`, { replace: true });
    changePhase('work');
    // 既に ready で本文ロード済みのシェルは placeholder のままなので本文を取り直す
    // (focus は表示中タブのみ。他は裏で差し替える)。
    // 復元ロードの途中で切り離されたら (別画面から戻る → 復元開始 → ✕ → 設定変更)、
    // 古いロードがタブ・設定・URL を上書きしないよう flow.detached を中断述語として渡す
    // (handleReadyShell 経由のロードと同じ扱い。flow はモジュールスコープで onDetach が
    // その場で detached=true にするので、参照を握れば以後の判定は正しい)。
    remaining
      .filter((p) => flow.loadedIds.has(p.id))
      .forEach((p) => {
        void handleLoadScenario(p.id, {
          focus: p.id === active,
          isAborted: () => flow.detached === true,
        }).catch((error) => {
          console.error('[ScenarioGenerator] Failed to restore ready shell:', p.id, error);
        });
      });
  };

  // ── Load scenario from URL (path parameter or query parameter) ──
  useEffect(() => {
    const scenarioId = scenarioIdFromPath || searchParams.get('scenario_id');

    // 別ページ (動画エディタ等) から戻ってきた / リロードした直後: state が空で、URL のシナリオが
    // 保存済みのタブ一覧に含まれていれば、URL のシナリオだけでなく元のタブをすべて元の順序で
    // 読み直す。URL のシナリオを表示中 (focus) にし、他はバックグラウンドで読む。
    if (scenarioId && !lastResult && restoreOpenTabsRef.current === 'idle') {
      const stored = readStoredOpenTabs();
      if (stored && stored.ids.length > 1 && stored.ids.includes(scenarioId)) {
        restoreOpenTabsRef.current = 'running';
        (async () => {
          // 内部の handleLoadScenario の finally でカウンタが 0 に戻る瞬間を作らないよう外側でも 1 加算
          setLoadingScenarioCount(c => c + 1);
          try {
            for (const id of stored.ids) {
              if (recentlyCreatedScenarioIdsRef.current.has(id)) continue;
              try {
                await handleLoadScenario(id, { focus: id === scenarioId });
              } catch (error) {
                console.error(`[ScenarioGenerator] Failed to restore scenario tab ${id}:`, error);
              }
            }
            setActiveScenarioId(scenarioId);
          } finally {
            setLoadingScenarioCount(c => c - 1);
            restoreOpenTabsRef.current = 'done';
          }
        })();
        return;
      }
      restoreOpenTabsRef.current = 'done';
    }
    if (restoreOpenTabsRef.current === 'running') return;

    if (scenarioId) {
      // mutation.onSuccess で直前に作ったシナリオなら、URL effect 側の handleLoadScenario は
      // 不要 (migrate で scope は既に組み立て済み)。誤発火による商品画像 auto-complete を防ぐ。
      // ref からは削除しない (React Strict Mode の二重発火 + alreadyLoaded false の隙間に
      // handleLoadScenario が走るのを防ぐため)。クリーンアップは mutation.onSuccess 側で
      // 5 秒後に行うので、ここで delete する必要はない。
      if (recentlyCreatedScenarioIdsRef.current.has(scenarioId)) {
        setActiveScenarioId(scenarioId);
        return;
      }
      const alreadyLoaded = lastResult?.scenarios.some(s => s.id === scenarioId);
      if (!alreadyLoaded) {
        handleLoadScenario(scenarioId).catch((error) => {
          console.error('[ScenarioGenerator] Failed to load scenario from URL:', error);
        });
      } else {
        setActiveScenarioId(scenarioId);
      }
    }

    // 特急モードから複数シナリオを一括読み込み (load_ids=id1,id2,...)
    const loadIds = searchParams.get('load_ids');
    if (loadIds) {
      const ids = loadIds.split(',').filter(Boolean);
      (async () => {
        for (const id of ids) {
          const already = lastResult?.scenarios.some(s => s.id === id);
          if (!already) {
            try {
              await handleLoadScenario(id);
            } catch (error) {
              console.error(`[ScenarioGenerator] Failed to load scenario ${id}:`, error);
            }
          }
        }
      })();
    }
  }, [scenarioIdFromPath, searchParams]);

  // ── Clear scenario ──────────────────────────────────────────
  const handleClearScenario = useCallback(() => {
    setLastResult(null);
    clearAllSceneState();
    setActiveScenarioId(null);
    setExpandedVideos({});
    setExpandedNarrations({});
    setIsProductSectionOpen(true);
    clearAllScopedAssets();
    setSelectedCasting(null);
    setAdditionalCastings([]);
    setSelectedSeedancePresetActor(null);
    setAdditionalSeedancePresetActors([]);
    clearAssets();
    setAsin('');
    // 素材を空にしたら取得済みフラグも捨てる。これを残すと同じ ASIN/商品を再選択
    // しても useProductMaterials が「取得済み」とみなして自動補完を skip し、商品が
    // 空のまま生成 → user_selected_asset_ids:[] が保存される (商品が消える事象)。
    setFetchedAsins(new Set());
    if (manualProductId) {
      setFetchedProductIds((prev) => {
        const next = new Set(prev);
        next.delete(manualProductId);
        return next;
      });
    }
    setManualProductId(null);
    setManualProductTitle(null);
    setManualProductImageUrl(null);
    changePhase('setup');
    navigate('/studio/video', { replace: true });
  }, [
    clearAllSceneState,
    setActiveScenarioId,
    setExpandedVideos, setExpandedNarrations,
    setIsProductSectionOpen,
    clearAllScopedAssets, setSelectedCasting, setAdditionalCastings,
    setSelectedSeedancePresetActor, setAdditionalSeedancePresetActors,
    setVisualStyle,
    setShootingMethod,
    setDialect,
    setTargetGender,
    setTargetAge,
    clearAssets,
    setAsin, manualProductId, setFetchedAsins, setFetchedProductIds,
    setManualProductId, setManualProductTitle, setManualProductImageUrl,
    changePhase, navigate,
  ]);

  // ── Form submit handler ─────────────────────────────────────
  const handleSubmit = useCallback((event: FormEvent) => {
    event.preventDefault();
    const assetPayload = selectedAssets
      .map((asset) => ({
        id: asset.id,
        source_url: asset.sourceUrl?.trim(),
      }))
      .filter((item): item is { id: string; source_url: string } => Boolean(item.source_url));
    const isSeedanceModel = (videoSettings.videoModel || '').startsWith('seedance');
    // アニメ風・3Dアニメ風は写実前提の人間 AI俳優/Seedance プリセットと併用しないため、
    // 人間俳優/プリセットは送らない (UI 側でも選択不可にしてある)。キャラクター俳優
    // (ethnicity='character') はスタイル・Seedance を問わず選択可 (計画書§4 P5)。
    const isStylized = visualStyleIsStylized;
    const castStyleCtx = { isSeedanceModel, isStylized, excludePeople };
    // 複数AI俳優対応: フルキャスト (主 + 追加) を順序保持で保持する。
    const fullCast: CastingActor[] = [
      ...(selectedCasting ? [selectedCasting] : []),
      ...additionalCastings,
    ];
    // スタイル×動画モデルの許可マトリクスで実際に送るキャストを絞り込む (P5-3):
    //   - excludePeople: 常に空
    //   - Seedance: キャラクター俳優のみ (スタイル非依存。CastingSection のキャラクター俳優
    //     ピッカーは isStylized でも常時表示され、プリセットとは別フィールドに排他で送る)
    //   - 非Seedance + 非写実系: キャラクター俳優のみ
    //   - 非Seedance + 実写系: 全俳優 (人間・キャラクターとも、従来どおり)
    const resolvedCastActors = resolveCastPayloadActors(fullCast, castStyleCtx);
    // 単数 casting (後方互換) は casting_models[0] と同値の不変条件を保つ。
    const castingPayload = resolvedCastActors[0] ? {
      id: resolvedCastActors[0].id,
      display_name: resolvedCastActors[0].display_name,
      gender: resolvedCastActors[0].gender,
      min_age: resolvedCastActors[0].min_age,
      max_age: resolvedCastActors[0].max_age,
      ethnicity: resolvedCastActors[0].ethnicity,
    } : undefined;
    const castingModelsPayload = resolvedCastActors.length > 0
      ? resolvedCastActors.map((c) => ({
          id: c.id,
          display_name: c.display_name,
          gender: c.gender,
          min_age: c.min_age,
          max_age: c.max_age,
          ethnicity: c.ethnicity,
        }))
      : undefined;
    // Seedance プリセット (BytePlus) は写実前提のため非写実系スタイルでは送らない
    // (キャラクター俳優は上の resolvedCastActors 経由で casting_models へ送る。排他制御は
    // CastingSection.tsx の handleToggleSeedancePresetActor/handleToggleSeedanceCharacterActor
    // 側で担保済みのため、ここではスタイル条件のみ見ればよい)。
    const sendSeedancePresetActors = shouldSendSeedancePresetActors(castStyleCtx);
    // 登録済み preset 選択時のみ Gemini プロンプト用メタを送る (カスタム入力時は属性不明)
    const seedancePresetPayload = (sendSeedancePresetActors && selectedSeedancePresetActor) ? {
      id: selectedSeedancePresetActor.id,
      display_name: selectedSeedancePresetActor.display_name,
      gender: selectedSeedancePresetActor.gender,
      min_age: selectedSeedancePresetActor.min_age,
      max_age: selectedSeedancePresetActor.max_age,
      ethnicity: selectedSeedancePresetActor.ethnicity,
    } : undefined;
    const seedancePresetActorId = sendSeedancePresetActors ? (selectedSeedancePresetActor?.id ?? undefined) : undefined;
    // 複数AI俳優対応: Seedance preset のフルキャスト (主 + 追加) を順序保持で送る。
    const fullPresetCast = [
      ...(selectedSeedancePresetActor ? [selectedSeedancePresetActor] : []),
      ...additionalSeedancePresetActors,
    ];
    const seedancePresetActorIds = (sendSeedancePresetActors && fullPresetCast.length > 0)
      ? fullPresetCast.map((a) => a.id)
      : undefined;
    const seedancePresetActorsPayload = (sendSeedancePresetActors && fullPresetCast.length > 0)
      ? fullPresetCast.map((a) => ({
          id: a.id,
          display_name: a.display_name,
          gender: a.gender,
          min_age: a.min_age,
          max_age: a.max_age,
          ethnicity: a.ethnicity,
        }))
      : undefined;
    // 「モーショングラフィックス」の3択 (§10.14)。ツリーで選べる値だけを送り、指定なし・field 未作成は省略する
    const motionGraphicsPayload = effectiveMotionGraphics(motionGraphics, motionGraphicsOptions) || undefined;
    // この生成の track ID (= 生成キューの行 ID) をここで採番し、variables に載せて渡す。
    // mutation のコールバックはすべて variables / context からこの ID を読むので、
    // 「切り離し → 即再実行」で先発と後発の ID が混ざらない (計画書 §4.3)。
    const submitGeneration = (basePayload: Record<string, unknown>) => {
      // 台本取り込み: imported_script を付け、count=1・dialect なしに揃える (読み込んでいなければそのまま)
      const payload = applyScriptImportToPayload(basePayload, importedScript);
      // 全編モーション (§10.5): BGM を先に決めるモーダルを挟む。本数は 1 に固定される (backend)
      if (isMotionEngine && requestMotionBgmChoice) {
        requestMotionBgmChoice((motionBgm) => {
          mutation.mutate({ clientJobId: newScenarioClientJobId(), payload: { ...payload, count: 1 }, motionBgm });
        });
        return;
      }
      mutation.mutate({ clientJobId: newScenarioClientJobId(), payload });
    };
    // 台本取り込みは全編モーションと併用できない (Phase 1。BE も 400 で拒否する)
    if (importedScript && isMotionEngine) {
      toast.error(SCRIPT_IMPORT_MOTION_REASON);
      return;
    }
    if (isMotionEngine && isLyricsTone) {
      toast.error('歌モノのトーンは全編モーショングラフィックスでは使えません。トーンを変えてください');
      return;
    }
    if (isMotionEngine && isMotionDurationTooLong) {
      toast.error(MOTION_DURATION_TOO_LONG_MESSAGE);
      return;
    }
    const parsedCharacterCount = characterCount.match(/\d+/)?.[0];
    shellGenerationSnapshotRef.current = {
      characterCountKey: characterCount || undefined,
      characterCount: parsedCharacterCount ? parseInt(parsedCharacterCount, 10) : undefined,
      // 送信 payload (castingModelsPayload) と同じキャスト種別を保存する (P5-3)。
      castingModels: resolvedCastActors.length > 0 ? resolvedCastActors : undefined,
      seedancePresetActors: (sendSeedancePresetActors && fullPresetCast.length > 0) ? fullPresetCast : undefined,
    };
    if (mode === 'asin') {
      if (!asin.trim()) {
        productSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setShowProductTooltip(true);
        return;
      }
      submitGeneration({
        source_type: 'asin',
        asin: asin.trim(),
        count: scenarioCount,
        model: scenarioModel,
        assets: assetPayload,
        aspect_ratio: videoSettings.aspectRatio,
        video_model: videoSettings.videoModel,
        output_resolution: videoSettings.resolution,
        tone: tone.trim() || undefined,
        duration: duration.trim() || undefined,
        character_count: characterCount || undefined,
        ..._behaviorPayloadFields(),
        casting: castingPayload,
        casting_models: castingModelsPayload,
        seedance_preset_actor: seedancePresetPayload,
        seedance_preset_actor_id: seedancePresetActorId,
        seedance_preset_actor_ids: seedancePresetActorIds,
        seedance_preset_actors: seedancePresetActorsPayload,
        additional_prompt: additionalPrompt.trim() || undefined,
        video_generation_mode: videoGenerationMode || undefined,
        // P2 step 5: production_mode/per_scene_default_mode を新設フィールドとして併記する。
        // video_generation_mode (レガシー3値) は互換のため引き続き送信する
        // (docs/features/scene-location-and-production-mode-plan.md §5-2)。
        ...deriveProductionModeWriteFields(videoGenerationMode),
        visual_style: visualStyle,
        shooting_method: shootingMethod,
        dialect,
        target_gender: targetGender || undefined,
        target_age: targetAge || undefined,
        motion_graphics: motionGraphicsPayload,
        avoid_faces: (!isStylized && avoidFaces) || undefined,
        exclude_people: excludePeople || undefined,
      });
    } else if (mode === 'manual') {
      if (!manualProductId) {
        productSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setShowProductTooltip(true);
        return;
      }
      submitGeneration({
        source_type: 'manual',
        product_id: manualProductId,
        count: scenarioCount,
        model: scenarioModel,
        assets: assetPayload,
        aspect_ratio: videoSettings.aspectRatio,
        video_model: videoSettings.videoModel,
        output_resolution: videoSettings.resolution,
        tone: tone.trim() || undefined,
        duration: duration.trim() || undefined,
        character_count: characterCount || undefined,
        ..._behaviorPayloadFields(),
        casting: castingPayload,
        casting_models: castingModelsPayload,
        seedance_preset_actor: seedancePresetPayload,
        seedance_preset_actor_id: seedancePresetActorId,
        seedance_preset_actor_ids: seedancePresetActorIds,
        seedance_preset_actors: seedancePresetActorsPayload,
        additional_prompt: additionalPrompt.trim() || undefined,
        video_generation_mode: videoGenerationMode || undefined,
        // P2 step 5: production_mode/per_scene_default_mode を新設フィールドとして併記する。
        // video_generation_mode (レガシー3値) は互換のため引き続き送信する
        // (docs/features/scene-location-and-production-mode-plan.md §5-2)。
        ...deriveProductionModeWriteFields(videoGenerationMode),
        visual_style: visualStyle,
        shooting_method: shootingMethod,
        dialect,
        target_gender: targetGender || undefined,
        target_age: targetAge || undefined,
        motion_graphics: motionGraphicsPayload,
        avoid_faces: (!isStylized && avoidFaces) || undefined,
        exclude_people: excludePeople || undefined,
      });
    } else {
      if (!productName.trim()) {
        toast.error('商品名を入力してください');
        return;
      }
      setManualProductPromptDismissed(false);
      submitGeneration({
        source_type: 'custom',
        count: scenarioCount,
        model: scenarioModel,
        assets: assetPayload,
        aspect_ratio: videoSettings.aspectRatio,
        video_model: videoSettings.videoModel,
        output_resolution: videoSettings.resolution,
        ..._behaviorPayloadFields(),
        casting: castingPayload,
        casting_models: castingModelsPayload,
        seedance_preset_actor: seedancePresetPayload,
        seedance_preset_actor_id: seedancePresetActorId,
        seedance_preset_actor_ids: seedancePresetActorIds,
        seedance_preset_actors: seedancePresetActorsPayload,
        additional_prompt: additionalPrompt.trim() || undefined,
        custom_input: {
          product_name: productName.trim(),
          brand_name: brandName.trim() || undefined,
          selling_points: sellingPoints
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
          goal: goal === 'price' ? '価格訴求' : goal === 'branding' ? 'ブランディング' : goal === 'awareness' ? '認知獲得' : undefined,
          target_audience: targetAudience.trim() || undefined,
          tone: tone.trim() || undefined,
          price_point: pricePoint.trim() || undefined,
          duration: duration.trim() || undefined,
          character_count: characterCount || undefined,
        },
        video_generation_mode: videoGenerationMode || undefined,
        // P2 step 5: production_mode/per_scene_default_mode を新設フィールドとして併記する。
        // video_generation_mode (レガシー3値) は互換のため引き続き送信する
        // (docs/features/scene-location-and-production-mode-plan.md §5-2)。
        ...deriveProductionModeWriteFields(videoGenerationMode),
        visual_style: visualStyle,
        shooting_method: shootingMethod,
        dialect,
        target_gender: targetGender || undefined,
        target_age: targetAge || undefined,
        motion_graphics: motionGraphicsPayload,
        avoid_faces: (!isStylized && avoidFaces) || undefined,
        exclude_people: excludePeople || undefined,
      });
    }
  }, [
    selectedAssets, selectedCasting, additionalCastings,
    selectedSeedancePresetActor, additionalSeedancePresetActors, mode, asin, manualProductId,
    productName, brandName, sellingPoints, goal, targetAudience,
    tone, pricePoint, duration, characterCount, additionalPrompt,
    scenarioCount, scenarioModel, videoSettings.aspectRatio,
    includeNarration, includeTelop, includeDialogue,
    // フェーズ6: tree 切替状態と表示ゲートを deps に含め、ツリー遅延ロード後の stale closure を防ぐ
    // (これらが変わると _behaviorPayloadFields の送信形が変わるため)。
    narrationTreeEnabled, narrationCategoryEnabled, dialogueTreeEnabled, dialogueCategoryEnabled,
    telopTreeEnabled, telopCategoryEnabled, _scenarioFieldConfig,
    productSelectRef, setShowProductTooltip, setManualProductPromptDismissed,
    mutation, toast, avoidFaces, visualStyle, visualStyleIsStylized, shootingMethod, dialect, excludePeople,
    motionGraphics, motionGraphicsOptions, targetGender, targetAge,
    isMotionEngine, isLyricsTone, isMotionDurationTooLong, requestMotionBgmChoice,
    importedScript,
  ]);

  // ── 最新マウント委譲のハンドラ束を更新 (毎 render) ─────────────
  ownDelegateRef.current.onGenerationSuccess = handleScenarioGenerationSuccess;
  ownDelegateRef.current.handleReadyShell = async (
    shellId: string,
    shellBatchId: string | undefined,
    clientJobId: string | null,
  ) => {
    // 実行中はこの track を消さない (実行中に finishTrack が来ても detached を見られるように)。
    if (clientJobId) beginHandler(clientJobId);
    try {
      // 切り離し判定はモジュールスコープの track を見るので、ロードを開始したマウントと
      // 切り離しを受けたマウントが違っても効く。
      // ⚠️ track は ID で引き直さず、ここで取ったオブジェクトを握る。handleLoadScenario 内の
      // 素材取得 (fetchAsinMaterials / getProduct の .then) は await されず、この関数の
      // finally (endHandler) より後まで残る。その間に切り離され残りのシェルも確定すると
      // track はレジストリから解放されるため、ID で引き直すと undefined → 「中断していない」
      // に戻り、遅れて返った素材が再び反映されてしまう。detachTrack はオブジェクトをその場で
      // 書き換えるので、参照を握れば解放後も detached を正しく読める。
      const track = clientJobId ? getTrack(clientJobId) : undefined;
      const isAborted = track ? () => track.detached === true : undefined;
      // ロード前に切り離されていたら「処理済み」として返す (ポーリングはリトライしない)。
      if (isAborted?.()) return true;
      const loaded = await handleLoadScenario(shellId, {
        focus: activeScenarioIdRef.current === shellId,
        isAborted,
      });
      // ロード後の再判定。await 中に切り離されていたら loaded が null でもロード失敗とは
      // 区別して「処理済み」扱いにする。
      if (isAborted?.()) return true;
      if (!loaded) return false;
      // 自動生成の直前にも再判定する (切り離した生成でポイントを使わない)。
      if (isAborted?.()) return true;
      runShellAutoGen(loaded, shellBatchId);
      return true;
    } finally {
      if (clientJobId) endHandler(clientJobId);
    }
  };
  // 切り離し時のタブ除去 (失敗トーストを出さない)。タブが空になったら設定画面へ戻して
  // すぐ次の生成を投入できる状態にする (§4.4)。
  ownDelegateRef.current.detachShellTabs = (ids: string[]) => {
    if (ids.length === 0) return;
    const removed = new Set(ids);
    const remaining = (lastResultRef.current?.scenarios ?? []).filter((s) => !removed.has(s.id));
    setLastResult((prev) => {
      if (!prev) return prev;
      const scenarios = prev.scenarios.filter((s) => !removed.has(s.id));
      if (scenarios.length === prev.scenarios.length) return prev;
      return scenarios.length > 0 ? { ...prev, scenarios } : null;
    });
    if (remaining.length === 0) {
      setActiveScenarioId(null);
      changePhase('setup');
      navigate('/studio/video', { replace: true });
      return;
    }
    // 切り離し対象以外のタブが残っている場合は、アクティブタブが消えたときだけ移す。
    if (activeScenarioIdRef.current && removed.has(activeScenarioIdRef.current)) {
      const next = remaining[0].id;
      setActiveScenarioId(next);
      navigate(`/studio/video/${next}`, { replace: true });
    }
  };
  ownDelegateRef.current.removeShellTab = (id: string, model: string) => {
    toast.error(`${scenarioModelDisplayName(model)} の生成に失敗しました`);
    setLastResult((prev) => (prev ? { ...prev, scenarios: prev.scenarios.filter((s) => s.id !== id) } : prev));
    // アクティブタブが消えたら残りの先頭へ移す (WorkPanel の空白表示を防ぐ)。
    if (activeScenarioIdRef.current === id) {
      const remaining = (lastResultRef.current?.scenarios ?? []).filter((s) => s.id !== id);
      const next = remaining[0]?.id ?? null;
      setActiveScenarioId(next);
      if (next) navigate(`/studio/video/${next}`, { replace: true });
    }
  };
  // シェル ready 後の自動生成後処理。通常生成 (onSuccess 後段) と同じトリガを、
  // ロード済みシナリオ 1 本に対して発火する。shellAutoGenFiredIds で 1 回きりに限定。
  const runShellAutoGen = (scenario: Scenario, shellBatchId?: string) => {
    if (shellAutoGenFiredIds.has(scenario.id)) return;
    shellAutoGenFiredIds.add(scenario.id);
    const arr = [scenario];
    // focus=false ではグローバル videoGenerationMode をシェルのモードへ反映しないため、
    // 自動生成の判定はシェル自身のモード (generation_metadata の production_mode/
    // per_scene_default_mode。無ければレガシー video_generation_mode/frame_mode から
    // 導出。P2 step 7-6・§9 分類A) で行う。表示中タブのモードに引きずられないようにする。
    const shellMode = deriveLoadedVideoGenerationMode(scenario);
    // 全編モーション (§10.5) はフレーム画像・絵コンテを使わないので自動生成しない (ナレーションだけ)
    const isMotionEngine = isMotionEngineScenario(scenario);
    // フレーム自動生成はフレームモードのときのみ。
    // 参照画像モード・ストーリーボードモードではスキップ (チェックボックスは非表示だが state は残存し得る)。
    if (autoGenerateFirstFrame && !isMotionEngine && shellMode !== 'reference' && shellMode !== 'storyboard') {
      handleBatchGenerateFirstFrames({ scenarios: arr, scenarioId: scenario.id });
    }
    // ストーリーボードを自動生成: シェルがストーリーボードモードのときのみ。
    // ready になったシェルから 1 本ずつ起動する (シナリオ別キューイング)。
    // batchId 付きでスロットを足し、全シェルの全絵コンテ完了で集約通知する。
    if (autoGenerateStoryboard && !isMotionEngine && shellMode === 'storyboard') {
      autoGenerateStoryboards(arr, shellBatchId);
    }
  };

  // 生成中判定は「attached な track があるか」に一本化する (計画書 §4.2)。
  //  - 生成中に別画面へ遷移して戻ると、新しい observer の mutation.isPending は false に
  //    なるが生成自体は進行中 (結果は上の委譲でこのマウントに届く) → track で拾う
  //  - シェルフローは mutation がシェル応答で完了しシェル別ポーリングだけが続く
  //    → 全シェル確定 (settleShell) の finishTrack まで track が attached のまま
  //  - 生成キューの ✕ で切り離した生成は、mutation が pending のままでも track が
  //    detached になるので生成ボタンが解放される
  const isGenerationInFlight = useAttachedScenarioTrack() !== null;
  // UI (SetupPanel の生成ボタン) は isPending しか見ないため、判別共用体の他フィールドは
  // 元のまま上書きする (型は cast)。切り離した mutation は完了まで isPending=true のままなので、
  // 補正は片方向ではなく**常に** track の判定で上書きする。
  // 切り離した生成の失敗はサイレント (§2) なので、直近のエラーが detached 由来なら
  // isError / error も落とす (ScenarioGenerator のエラーバナーを出さない)。
  // observer が表示しているエラーが、切り離した生成のものか (variables は observer が
  // 追跡している mutation のもの)。後発の生成を投入すると observer はそちらを向くので、
  // 切り離した生成のエラーは自然に表示対象から外れる。
  const suppressDetachedError = mutation.isError
    && detachedErrorJobIdRef.current !== null
    && mutation.variables?.clientJobId === detachedErrorJobIdRef.current;
  const mutationForUi = useMemo(
    () => (mutation.isPending === isGenerationInFlight && !suppressDetachedError
      ? mutation
      : ({
          ...mutation,
          isPending: isGenerationInFlight,
          ...(suppressDetachedError ? { isError: false, error: null } : {}),
        } as unknown as typeof mutation)),
    [mutation, isGenerationInFlight, suppressDetachedError],
  );

  return {
    // State
    phase,
    hasTransitioned,
    changePhase,
    isLoadingScenario,

    // Mutation
    mutation: mutationForUi,

    // Handlers
    handleLoadScenario,
    handleLoadScenarios,
    handleClearScenario,
    handleSubmit,
  };
}
